import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { QRCode } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { VOLUNTEER_CATEGORIES } from '../constants/volunteerCategories';
import { supabase } from '../services/supabase';
import { useVolunteerCategories } from '../hooks/useVolunteerCategories';
import { PublicEventRegistrationsEventPage } from './PublicEventRegistrationsPage';
import {
  IMHIM_WEBSITE_SITE_KEY,
  type EventOrderForm,
  type EventOrderFormField,
  type EventTicketType,
  useEventAppointmentSlots,
  useEventOrderForm,
  useEvents,
  usePublicWebsiteEventLink,
  useSaveEventAppointmentSlots,
  useEventTicketTypes,
  useSaveEventOrderForm,
  useSaveEventTicketTypes,
  useSavePublicWebsiteEventLink,
  useUpdateEvent,
} from '../hooks/useEvents';
import { useUpdateEventRequest } from '../hooks/useEventRequests';
import { EventMediaEditor } from '../components/EventMediaEditor';
import EventAdminsPanel from '../components/events/EventAdminsPanel';
import EventWaiverList from '../components/events/EventWaiverList';
import { EventMediaItem, getPrimaryImageUrl, normalizeEventMediaItems } from '../utils/eventMedia';

const DEFAULT_EVENT_FORM_FIELDS: Array<{
  key: string;
  label: string;
  field_type: EventOrderFormField['field_type'];
  placeholder: string;
  options?: string[];
  help_text?: string;
}> = [
  { key: 'first_name', label: 'First Name', field_type: 'short_text', placeholder: 'Enter first name' },
  { key: 'last_name', label: 'Last Name', field_type: 'short_text', placeholder: 'Enter last name' },
  { key: 'email', label: 'Email', field_type: 'email', placeholder: 'name@example.com' },
  { key: 'phone', label: 'Phone', field_type: 'phone', placeholder: '(555) 123-4567' },
  {
    key: 'single_or_group',
    label: 'Single or Group',
    field_type: 'select',
    placeholder: 'Select one',
    options: ['Single', 'Group'],
    help_text: 'Choose Group to provide multiple attendee contacts.',
  },
];

const DEFAULT_FIELD_KEY_ALIASES: Record<string, string> = {
  first_name: 'first_name',
  firstname: 'first_name',
  last_name: 'last_name',
  lastname: 'last_name',
  email: 'email',
  email_address: 'email',
  phone: 'phone',
  phone_number: 'phone',
  mobile: 'phone',
  mobile_number: 'phone',
  single_or_group: 'single_or_group',
  registration_type: 'single_or_group',
  attendee_type: 'single_or_group',
  group_type: 'single_or_group',
};

const EVENT_QR_FOREGROUND = '#111827';
const EVENT_QR_BACKGROUND = '#ffffff';
const EVENT_QR_DOWNLOAD_SIZE = 2048;
const EVENT_QR_DOWNLOAD_INSET = 224;

function normalizeFieldIdentity(value?: string | null) {
  return (value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function buildDefaultOrderFields(): EventOrderFormField[] {
  return DEFAULT_EVENT_FORM_FIELDS.map((field, index) => ({
    field_key: field.key,
    label: field.label,
    field_type: field.field_type,
    is_required: true,
    sort_order: index,
    options: field.options ?? [],
    validation: {},
    visibility_rules: {},
    placeholder: field.placeholder,
    help_text: field.help_text ?? '',
  }));
}

function resolveDefaultFieldKey(field: Pick<EventOrderFormField, 'field_key' | 'label'>) {
  const byKey = DEFAULT_FIELD_KEY_ALIASES[normalizeFieldIdentity(field.field_key)];
  const byLabel = DEFAULT_FIELD_KEY_ALIASES[normalizeFieldIdentity(field.label)];
  return byKey ?? byLabel ?? null;
}

function isDefaultOrderField(field: Pick<EventOrderFormField, 'field_key' | 'label'>) {
  return Boolean(resolveDefaultFieldKey(field));
}

function normalizeDefaultField(
  defaultField: (typeof DEFAULT_EVENT_FORM_FIELDS)[number],
  existing?: EventOrderFormField
): EventOrderFormField {
  return {
    ...existing,
    field_key: defaultField.key,
    label: defaultField.label,
    field_type: defaultField.field_type,
    is_required: existing?.is_required ?? true,
    sort_order: 0,
    options: defaultField.options ?? existing?.options ?? [],
    validation: existing?.validation ?? {},
    visibility_rules: existing?.visibility_rules ?? {},
    placeholder: defaultField.placeholder,
    help_text: defaultField.help_text ?? existing?.help_text ?? '',
    field_group: 'registration',
  };
}

function fieldGroupOf(field: Pick<EventOrderFormField, 'field_group'>) {
  return field.field_group === 'waiver' ? 'waiver' : 'registration';
}

function enforceDefaultOrderFields(fields?: EventOrderFormField[] | null): EventOrderFormField[] {
  const defaultsByKey = new Map(DEFAULT_EVENT_FORM_FIELDS.map((field) => [field.key, field]));
  const seenDefaults = new Set<string>();
  const normalizedFields: EventOrderFormField[] = [];

  for (const field of fields ?? []) {
    const canonicalDefaultKey = resolveDefaultFieldKey(field);
    if (canonicalDefaultKey) {
      if (seenDefaults.has(canonicalDefaultKey)) continue;
      const defaultField = defaultsByKey.get(canonicalDefaultKey);
      if (!defaultField) continue;
      seenDefaults.add(canonicalDefaultKey);
      normalizedFields.push(normalizeDefaultField(defaultField, field));
      continue;
    }

    normalizedFields.push({
      ...field,
      field_group: fieldGroupOf(field),
      options: field.options ?? [],
      validation: field.validation ?? {},
      visibility_rules: field.visibility_rules ?? {},
      placeholder: field.placeholder ?? '',
      help_text: field.help_text ?? '',
    });
  }

  for (const defaultField of DEFAULT_EVENT_FORM_FIELDS) {
    if (seenDefaults.has(defaultField.key)) continue;
    normalizedFields.push(normalizeDefaultField(defaultField));
  }

  return normalizedFields.map((field, index) => ({ ...field, sort_order: index }));
}

function readCollectPerAttendee(field: Pick<EventOrderFormField, 'validation'>) {
  if (!field.validation || typeof field.validation !== 'object' || Array.isArray(field.validation)) return false;
  return Boolean((field.validation as Record<string, unknown>).collect_per_attendee);
}

function withCollectPerAttendeeValidation(
  field: Pick<EventOrderFormField, 'validation'>,
  collectPerAttendee: boolean,
): Record<string, unknown> {
  const current =
    field.validation && typeof field.validation === 'object' && !Array.isArray(field.validation)
      ? (field.validation as Record<string, unknown>)
      : {};
  return {
    ...current,
    collect_per_attendee: collectPerAttendee,
  };
}

type EventTypeOption = 'Event' | 'General Announcement';
type SetupNotice = {
  kind: 'success' | 'error' | 'info';
  message: string;
};
type ValidationResult = {
  ok: boolean;
  message?: string;
};
type AppointmentSlotDraft = {
  id?: string;
  label: string;
  starts_at: string;
  ends_at: string;
  capacity: number;
  status: 'active' | 'inactive';
  sort_order: number;
};
type SetupTab =
  | 'general'
  | 'appointments'
  | 'ticketing'
  | 'form'
  | 'waiver'
  | 'waiver-list'
  | 'registrants'
  | 'tshirts'
  | 'communication'
  | 'reporting'
  | 'admins';
type ReportingRegistrationRow = {
  id: string;
  quantity: number;
  ticket_type_id: string | null;
  ticket_type_name: string | null;
  status: string | null;
  amount_paid_cents?: number | null;
  total_amount_cents?: number | null;
};
type ReportingRegistrationTicketRow = {
  registration_id: string;
  status: string | null;
  checked_in_at: string | null;
};
type ReportingTicketTypeRow = {
  id: string;
  name: string;
  price_cents: number;
  deposit_amount_cents: number;
  status: string;
};
type ReportingTicketChangeRow = {
  id: string;
  registration_id: string;
  old_ticket_type_name: string;
  new_ticket_type_name: string;
  difference_cents: number;
  status: string;
  payment_status: string;
};

function normalizeEventType(value?: string | null): EventTypeOption {
  const normalized = (value ?? '')
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (normalized === 'general announcement' || normalized === 'general annoucement') {
    return 'General Announcement';
  }
  return 'Event';
}

function buildWebsiteEventRegistrationUrl(eventId: string) {
  const encodedEventId = encodeURIComponent(eventId);
  const path = `/events/register/${encodedEventId}`;
  const configuredWebsiteUrl = (process.env.REACT_APP_WEBSITE_URL ?? '').trim();
  const fallbackBaseUrl = 'https://updates.ncof.tv';
  const baseUrl = configuredWebsiteUrl || fallbackBaseUrl;
  return new URL(path, `${baseUrl.replace(/\/$/, '')}/`).toString();
}

function buildWebsiteTicketRegistrationUrl(eventId: string, ticketTypeId: string) {
  const registrationUrl = new URL(buildWebsiteEventRegistrationUrl(eventId));
  registrationUrl.searchParams.set('ticketTypeId', ticketTypeId);
  return registrationUrl.toString();
}

function parseOptionLines(value: string) {
  const seen = new Set<string>();
  const parsed: string[] = [];
  for (const token of value.split(/\r?\n|,/g)) {
    const next = token.trim();
    if (!next) continue;
    const identity = next.toLowerCase();
    if (seen.has(identity)) continue;
    seen.add(identity);
    parsed.push(next);
  }
  return parsed;
}

function toLocalDateTimeInput(value?: string | null) {
  const normalized = String(value || '').trim();
  if (!normalized) return '';
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

function toIsoFromLocalDateTime(value?: string | null) {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function buildDefaultAppointmentSlotDraft(
  seed: number,
  eventStartDate?: string | null,
  eventStartTime?: string | null,
  eventEndDate?: string | null,
  eventEndTime?: string | null,
): AppointmentSlotDraft {
  const fallbackStart = eventStartDate
    ? `${eventStartDate}T${eventStartTime || '09:00'}`
    : '';
  const fallbackEnd = eventEndDate
    ? `${eventEndDate}T${eventEndTime || eventStartTime || '10:00'}`
    : '';
  return {
    label: '',
    starts_at: fallbackStart,
    ends_at: fallbackEnd,
    capacity: 1,
    status: 'active',
    sort_order: seed,
  };
}

function normalizeFieldOptions(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === 'string' ? item.trim() : String(item ?? '').trim()))
      .filter(Boolean);
  }

  if (typeof value === 'string') {
    return parseOptionLines(value);
  }

  return [];
}

type FieldOptionsEditorProps = {
  options?: string[];
  disabled?: boolean;
  onChange: (nextOptions: string[]) => void;
};

const FieldOptionsEditor: React.FC<FieldOptionsEditorProps> = ({ options = [], disabled, onChange }) => {
  const [pendingOption, setPendingOption] = React.useState('');
  const [bulkOptions, setBulkOptions] = React.useState('');

  const addSingleOption = React.useCallback(() => {
    const nextLabel = pendingOption.trim();
    if (!nextLabel) return;
    const exists = options.some((option) => option.trim().toLowerCase() === nextLabel.toLowerCase());
    if (exists) {
      setPendingOption('');
      return;
    }
    onChange([...options, nextLabel]);
    setPendingOption('');
  }, [onChange, options, pendingOption]);

  const removeOption = React.useCallback(
    (target: string) => {
      onChange(options.filter((option) => option !== target));
    },
    [onChange, options]
  );

  const applyBulkOptions = React.useCallback(() => {
    const parsed = parseOptionLines(bulkOptions);
    if (parsed.length === 0) return;
    const existing = new Set(options.map((option) => option.trim().toLowerCase()));
    const merged = [...options];
    for (const parsedOption of parsed) {
      const key = parsedOption.toLowerCase();
      if (existing.has(key)) continue;
      existing.add(key);
      merged.push(parsedOption);
    }
    onChange(merged);
    setBulkOptions('');
  }, [bulkOptions, onChange, options]);

  return (
    <FieldOptionsWrap>
      <OptionHintText>Options</OptionHintText>
      <OptionChipList>
        {options.length === 0 ? <OptionEmptyText>No options added yet.</OptionEmptyText> : null}
        {options.map((option) => (
          <OptionChip key={option}>
            <span>{option}</span>
            <OptionRemoveButton type="button" onClick={() => removeOption(option)} disabled={disabled}>
              ×
            </OptionRemoveButton>
          </OptionChip>
        ))}
      </OptionChipList>
      <OptionInputRow>
        <OptionTextInput
          value={pendingOption}
          placeholder="Add option and press Enter"
          onChange={(event) => setPendingOption(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Enter') return;
            event.preventDefault();
            addSingleOption();
          }}
          disabled={disabled}
        />
        <SecondaryActionButton type="button" onClick={addSingleOption} disabled={disabled}>
          Add
        </SecondaryActionButton>
      </OptionInputRow>
      <OptionBulkArea
        rows={3}
        value={bulkOptions}
        placeholder="Paste multiple options (comma or new line separated)"
        onChange={(event) => setBulkOptions(event.target.value)}
        disabled={disabled}
      />
      <ButtonRow>
        <SecondaryActionButton type="button" onClick={applyBulkOptions} disabled={disabled}>
          Add Bulk Options
        </SecondaryActionButton>
      </ButtonRow>
    </FieldOptionsWrap>
  );
};

const EventSetupPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolveTab = React.useCallback(
    (tabValue: string | null): SetupTab => {
      if (tabValue === 'appointments') return 'appointments';
      if (tabValue === 'ticketing') return 'ticketing';
      if (tabValue === 'form') return 'form';
      if (tabValue === 'waiver') return 'waiver';
      if (tabValue === 'waiver-list') return 'waiver-list';
      if (tabValue === 'registrants') return 'registrants';
      if (tabValue === 'tshirts') return 'tshirts';
      if (tabValue === 'communication') return 'communication';
      if (tabValue === 'reporting') return 'reporting';
      if (tabValue === 'admins') return 'admins';
      return 'general';
    },
    []
  );
  const [activeTab, setActiveTab] = React.useState<SetupTab>(() => resolveTab(searchParams.get('tab')));
  const { data: events, isLoading: isLoadingEvents, error: eventsError } = useEvents(false);
  const selectedEvent = React.useMemo(
    () => events?.find((event) => event.id === eventId) ?? null,
    [events, eventId]
  );

  const [ticketTypesDraft, setTicketTypesDraft] = React.useState<EventTicketType[]>([]);
  const [orderFormDraft, setOrderFormDraft] = React.useState<EventOrderForm>({
    is_enabled: false,
    waiver_enabled: false,
    fields: buildDefaultOrderFields(),
  });
  const [formPayloadError, setFormPayloadError] = React.useState<string | null>(null);
  const [eventDetailsDraft, setEventDetailsDraft] = React.useState({
    title: '',
    description: '',
    start_date: '',
    end_date: '',
    start_time: '',
    end_time: '',
    image_url: '',
    media_items: [] as EventMediaItem[],
    location: '',
    ministry: '',
    category: 'Event',
    status: 'draft',
    is_registration_required: false,
    hide_registration_details_when_no_public_tickets: false,
    appointments_enabled: false,
    appointment_booking_mode: 'single' as 'single' | 'multiple',
    paypal_account: 'ncof' as 'ncof' | 'event_paypal' | 'none',
  });
  const [appointmentSlotsDraft, setAppointmentSlotsDraft] = React.useState<AppointmentSlotDraft[]>([]);
  const [isMinistryDropdownOpen, setIsMinistryDropdownOpen] = React.useState(false);
  const [isUploadingBanner, setIsUploadingBanner] = React.useState(false);
  const [editingTicketIndex, setEditingTicketIndex] = React.useState<number | null>(null);
  const [ticketEditor, setTicketEditor] = React.useState({
    name: '',
    description: '',
    price_cents: 0,
    deposit_amount_cents: 0,
    capacity: 500,
    status: 'active' as EventTicketType['status'],
    per_order_limit: 10,
    is_special: false,
    is_sponsor_ticket: false,
    is_locked: false,
    access_code: '',
    is_exclusive: false,
    allowed_emails: '',
    is_group_ticket: false,
    min_group_size: 6,
    requires_registration_form: true,
  });
  const [ticketPriceInput, setTicketPriceInput] = React.useState('0.00');
  const [ticketDepositInput, setTicketDepositInput] = React.useState('0.00');
  const [salesWindow, setSalesWindow] = React.useState({
    sales_start: '',
    sales_end: '',
  });
  const [collapsedFields, setCollapsedFields] = React.useState<Record<string, boolean>>({});
  const [isPublishQrModalOpen, setIsPublishQrModalOpen] = React.useState(false);
  const [setupNotice, setSetupNotice] = React.useState<SetupNotice | null>(null);
  const [generalValidationError, setGeneralValidationError] = React.useState<string | null>(null);
  const [appointmentValidationError, setAppointmentValidationError] = React.useState<string | null>(null);
  const [ticketValidationError, setTicketValidationError] = React.useState<string | null>(null);
  const [formValidationError, setFormValidationError] = React.useState<string | null>(null);
  const persistentQrContainerRef = React.useRef<HTMLDivElement | null>(null);
  const modalQrContainerRef = React.useRef<HTMLDivElement | null>(null);

  const { data: ticketTypesData, isLoading: isLoadingTicketTypes } = useEventTicketTypes(eventId);
  const { data: appointmentSlotsData, isLoading: isLoadingAppointmentSlots } = useEventAppointmentSlots(eventId);
  const { data: orderFormData, isLoading: isLoadingOrderForm } = useEventOrderForm(eventId);
  const { data: volunteerCategories } = useVolunteerCategories();
  const { mutateAsync: saveTicketTypes, isPending: isSavingTicketTypes } = useSaveEventTicketTypes(eventId);
  const { mutateAsync: saveAppointmentSlots, isPending: isSavingAppointmentSlots } = useSaveEventAppointmentSlots(eventId);
  const { mutateAsync: saveOrderForm, isPending: isSavingOrderForm } = useSaveEventOrderForm(eventId);
  const { mutateAsync: updateEvent, isPending: isSavingEventDetails } = useUpdateEvent();
  const { mutateAsync: updateEventRequest } = useUpdateEventRequest();
  const { data: imhimWebsiteLink } = usePublicWebsiteEventLink(IMHIM_WEBSITE_SITE_KEY);
  const { mutateAsync: saveImhimWebsiteLink } = useSavePublicWebsiteEventLink(IMHIM_WEBSITE_SITE_KEY);
  const [linkToImhimWebsite, setLinkToImhimWebsite] = React.useState(false);
  const websiteEventUrl = React.useMemo(
    () => (eventId ? buildWebsiteEventRegistrationUrl(eventId) : ''),
    [eventId]
  );
  const ministryOptions = React.useMemo(() => {
    const categories =
      volunteerCategories && volunteerCategories.length > 0
        ? volunteerCategories.map((category) => category.label).filter(Boolean)
        : VOLUNTEER_CATEGORIES.map((category) => category.label).filter(Boolean);
    return Array.from(new Set(categories)).sort((a, b) => a.localeCompare(b));
  }, [volunteerCategories]);
  const filteredMinistryOptions = React.useMemo(() => {
    const query = eventDetailsDraft.ministry.trim().toLowerCase();
    if (!query) return ministryOptions;
    return ministryOptions.filter((option) => option.toLowerCase().includes(query));
  }, [eventDetailsDraft.ministry, ministryOptions]);

  React.useEffect(() => {
    const nextTab = resolveTab(searchParams.get('tab'));
    setActiveTab((current) => (current === nextTab ? current : nextTab));
  }, [resolveTab, searchParams]);

  const handleTabChange = React.useCallback(
    (nextTab: SetupTab) => {
      setActiveTab(nextTab);
      const nextSearchParams = new URLSearchParams(searchParams);
      if (nextTab === 'general') nextSearchParams.delete('tab');
      else nextSearchParams.set('tab', nextTab);
      setSearchParams(nextSearchParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const currentSetupPath = React.useMemo(() => {
    if (!eventId) return '/events';
    const currentSearchParams = new URLSearchParams(searchParams);
    if (activeTab === 'general') currentSearchParams.delete('tab');
    else currentSearchParams.set('tab', activeTab);
    const query = currentSearchParams.toString();
    return `/events/setup/${eventId}${query ? `?${query}` : ''}`;
  }, [activeTab, eventId, searchParams]);

  const handleOpenSendEmailPage = React.useCallback(() => {
    if (!eventId) return;
    const nextSearchParams = new URLSearchParams();
    nextSearchParams.set('mode', 'bulk');
    nextSearchParams.set('scope', 'filtered');
    nextSearchParams.set('returnTo', currentSetupPath);
    navigate(`/events/public-registrations/${eventId}/email?${nextSearchParams.toString()}`);
  }, [currentSetupPath, eventId, navigate]);

  const {
    data: reportingRegistrations = [],
    isLoading: isLoadingReportingRegistrations,
    error: reportingRegistrationsError,
  } = useQuery({
    queryKey: ['event-setup-reporting-registrations', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_registrations')
        .select('id, quantity, ticket_type_id, ticket_type_name, status, amount_paid_cents, total_amount_cents')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data || []) as ReportingRegistrationRow[]).map((row) => ({
        ...row,
        quantity: Math.max(1, Number(row.quantity || 1)),
      }));
    },
  });
  const {
    data: reportingRegistrationTickets = [],
    isLoading: isLoadingReportingRegistrationTickets,
    error: reportingRegistrationTicketsError,
  } = useQuery({
    queryKey: ['event-setup-reporting-registration-tickets', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_registration_tickets')
        .select('registration_id, status, checked_in_at')
        .eq('event_id', eventId);
      if (error) throw new Error(error.message);
      return (data || []) as ReportingRegistrationTicketRow[];
    },
  });
  const {
    data: reportingTicketTypes = [],
    isLoading: isLoadingReportingTicketTypes,
    error: reportingTicketTypesError,
  } = useQuery({
    queryKey: ['event-setup-reporting-ticket-types', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('event_ticket_types')
        .select('id, name, price_cents, deposit_amount_cents, status')
        .eq('event_id', eventId)
        .order('sort_order', { ascending: true });
      if (error) throw new Error(error.message);
      return ((data || []) as ReportingTicketTypeRow[]).map((row) => ({
        ...row,
        price_cents: Number(row.price_cents || 0),
        deposit_amount_cents: Number(row.deposit_amount_cents || 0),
      }));
    },
  });
  const {
    data: reportingTicketChanges = [],
    isLoading: isLoadingReportingTicketChanges,
    error: reportingTicketChangesError,
  } = useQuery({
    queryKey: ['event-setup-reporting-ticket-changes', eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('public_event_ticket_change_requests')
        .select('id, registration_id, old_ticket_type_name, new_ticket_type_name, difference_cents, status, payment_status')
        .eq('event_id', eventId)
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return ((data || []) as ReportingTicketChangeRow[]).map((row) => ({
        ...row,
        difference_cents: Number(row.difference_cents || 0),
      }));
    },
  });

  React.useEffect(() => {
    if (!ticketTypesData) {
      setTicketTypesDraft([]);
      return;
    }
    setTicketTypesDraft(
      ticketTypesData.map((ticketType, index) => ({
        ...ticketType,
        deposit_amount_cents: Number(ticketType.deposit_amount_cents ?? 0),
        is_special: Boolean(ticketType.is_special),
        is_sponsor_ticket: Boolean(ticketType.is_sponsor_ticket),
        is_locked: Boolean(ticketType.is_locked),
        access_code: ticketType.access_code ?? null,
        is_exclusive: Boolean(ticketType.is_exclusive),
        exclusive_enforce_once: Boolean(ticketType.exclusive_enforce_once ?? true),
        allowed_emails: Array.isArray(ticketType.allowed_emails) ? ticketType.allowed_emails : [],
        requires_registration_form: ticketType.requires_registration_form !== false,
        is_group_ticket: Boolean(ticketType.is_group_ticket),
        min_group_size: Math.max(1, Number(ticketType.min_group_size ?? 6)),
        sort_order: Number.isFinite(ticketType.sort_order) ? ticketType.sort_order : index,
      }))
    );
  }, [ticketTypesData]);

  React.useEffect(() => {
    if (!orderFormData) {
      setOrderFormDraft({ is_enabled: false, waiver_enabled: false, fields: buildDefaultOrderFields() });
      return;
    }
    setOrderFormDraft({
      ...orderFormData,
      waiver_enabled: orderFormData.waiver_enabled === true,
      fields: enforceDefaultOrderFields(orderFormData.fields ?? []),
    });
    setFormPayloadError(null);
  }, [orderFormData]);

  React.useEffect(() => {
    if (!appointmentSlotsData) {
      setAppointmentSlotsDraft([]);
      return;
    }
    setAppointmentSlotsDraft(
      appointmentSlotsData.map((slot, index) => ({
        id: slot.id,
        label: slot.label ?? '',
        starts_at: toLocalDateTimeInput(slot.starts_at),
        ends_at: toLocalDateTimeInput(slot.ends_at),
        capacity: Math.max(1, Number(slot.capacity || 1)),
        status: slot.status === 'inactive' ? 'inactive' : 'active',
        sort_order: Number.isFinite(slot.sort_order) ? slot.sort_order : index,
      }))
    );
  }, [appointmentSlotsData]);

  React.useEffect(() => {
    if (!selectedEvent) {
      setEventDetailsDraft({
        title: '',
        description: '',
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        image_url: '',
        media_items: [],
        location: '',
        ministry: '',
        category: 'Event',
        status: 'draft',
        is_registration_required: false,
        hide_registration_details_when_no_public_tickets: false,
        appointments_enabled: false,
        appointment_booking_mode: 'single',
        paypal_account: 'ncof',
      });
      return;
    }

    const mediaItems = normalizeEventMediaItems(selectedEvent.media_items, selectedEvent.image_url);
    setEventDetailsDraft({
      title: selectedEvent.title ?? '',
      description: selectedEvent.description ?? '',
      start_date: selectedEvent.start_date ?? '',
      end_date: selectedEvent.end_date ?? selectedEvent.start_date ?? '',
      start_time: selectedEvent.start_time ? selectedEvent.start_time.slice(0, 5) : '',
      end_time: selectedEvent.end_time ? selectedEvent.end_time.slice(0, 5) : '',
      image_url: selectedEvent.image_url ?? '',
      media_items: mediaItems,
      location: selectedEvent.location ?? '',
      ministry: selectedEvent.ministry ?? '',
      category: normalizeEventType(selectedEvent.category),
      status: selectedEvent.status === 'approved' ? 'approved' : selectedEvent.status ?? 'draft',
      is_registration_required: Boolean(selectedEvent.is_registration_required),
      hide_registration_details_when_no_public_tickets: Boolean(
        selectedEvent.hide_registration_details_when_no_public_tickets
      ),
      appointments_enabled: Boolean(selectedEvent.appointments_enabled),
      appointment_booking_mode: selectedEvent.appointment_booking_mode === 'multiple' ? 'multiple' : 'single',
      paypal_account:
        selectedEvent.paypal_account === 'event_paypal'
          ? 'event_paypal'
          : selectedEvent.paypal_account === 'none'
            ? 'none'
            : 'ncof',
    });
  }, [selectedEvent]);

  React.useEffect(() => {
    if (!eventId) {
      setLinkToImhimWebsite(false);
      return;
    }
    setLinkToImhimWebsite(String(imhimWebsiteLink?.event_id || '') === eventId);
  }, [eventId, imhimWebsiteLink?.event_id]);

  React.useEffect(() => {
    if (!selectedEvent) return;
    setSalesWindow((prev) => ({
      ...prev,
      sales_start: prev.sales_start || selectedEvent.start_date || '',
      sales_end: prev.sales_end || selectedEvent.start_date || '',
    }));
  }, [selectedEvent]);

  const startCreateTicket = () => {
    setEditingTicketIndex(null);
    setTicketValidationError(null);
    setTicketPriceInput('0.00');
    setTicketDepositInput('0.00');
    setTicketEditor({
      name: '',
      description: '',
      price_cents: 0,
      deposit_amount_cents: 0,
      capacity: 500,
      status: 'active',
      per_order_limit: 10,
      is_special: false,
    is_sponsor_ticket: false,
      is_locked: false,
      access_code: '',
      is_exclusive: false,
      allowed_emails: '',
      is_group_ticket: false,
      min_group_size: 6,
      requires_registration_form: true,
    });
  };

  const startEditTicket = (index: number) => {
    const ticket = ticketTypesDraft[index];
    if (!ticket) return;
    setEditingTicketIndex(index);
    setTicketValidationError(null);
    const nextPriceCents = Number(ticket.price_cents ?? 0);
    const nextDepositCents = Math.min(nextPriceCents, Number(ticket.deposit_amount_cents ?? 0));
    setTicketPriceInput(formatTicketPriceInput(nextPriceCents));
    setTicketDepositInput(formatTicketPriceInput(nextDepositCents));
    setTicketEditor({
      name: ticket.name,
      description: ticket.description ?? '',
      price_cents: nextPriceCents,
      deposit_amount_cents: nextDepositCents,
      capacity: ticket.capacity ?? 500,
      status: ticket.status,
      per_order_limit: Number(ticket.per_order_limit ?? 10),
      is_special: Boolean(ticket.is_special),
      is_sponsor_ticket: Boolean(ticket.is_sponsor_ticket),
      is_locked: Boolean(ticket.is_locked),
      access_code: ticket.access_code ?? '',
      is_exclusive: Boolean(ticket.is_exclusive),
      allowed_emails: (ticket.allowed_emails ?? []).join('\n'),
      is_group_ticket: Boolean(ticket.is_group_ticket),
      min_group_size: Math.max(1, Number(ticket.min_group_size ?? 6)),
      requires_registration_form: ticket.requires_registration_form !== false,
    });
    setSalesWindow((prev) => ({
      ...prev,
      sales_start: ticket.sales_start?.slice(0, 10) || prev.sales_start,
      sales_end: ticket.sales_end?.slice(0, 10) || prev.sales_end,
    }));
  };

  const cancelTicketEditor = () => {
    startCreateTicket();
  };

  const handleTicketPriceChange = React.useCallback((rawValue: string) => {
    const sanitizedPrice = sanitizeTicketPriceInput(rawValue);
    setTicketPriceInput(sanitizedPrice);
    const nextPriceCents = parseTicketPriceInputToCents(sanitizedPrice);
    setTicketEditor((prev) => ({
      ...prev,
      price_cents: nextPriceCents,
      deposit_amount_cents: Math.min(Number(prev.deposit_amount_cents || 0), nextPriceCents),
    }));
  }, []);

  const handleTicketPriceBlur = React.useCallback(() => {
    const normalizedPrice = formatTicketPriceInput(ticketEditor.price_cents);
    setTicketPriceInput(normalizedPrice);
    const normalizedDeposit = formatTicketPriceInput(
      Math.min(Number(ticketEditor.price_cents || 0), Number(ticketEditor.deposit_amount_cents || 0)),
    );
    setTicketDepositInput(normalizedDeposit);
  }, [ticketEditor.deposit_amount_cents, ticketEditor.price_cents]);

  const handleTicketDepositChange = React.useCallback((rawValue: string) => {
    const sanitizedDeposit = sanitizeTicketPriceInput(rawValue);
    setTicketDepositInput(sanitizedDeposit);
    setTicketEditor((prev) => ({
      ...prev,
      deposit_amount_cents: Math.min(
        Number(prev.price_cents || 0),
        parseTicketPriceInputToCents(sanitizedDeposit),
      ),
    }));
  }, []);

  const handleTicketDepositBlur = React.useCallback(() => {
    const normalizedDeposit = formatTicketPriceInput(
      Math.min(Number(ticketEditor.price_cents || 0), Number(ticketEditor.deposit_amount_cents || 0)),
    );
    setTicketDepositInput(normalizedDeposit);
  }, [ticketEditor.deposit_amount_cents, ticketEditor.price_cents]);

  const upsertTicketType = () => {
    if (!ticketEditor.name.trim()) {
      setTicketValidationError('Ticket type name is required.');
      setSetupNotice({ kind: 'error', message: 'Ticket type name is required.' });
      return;
    }
    if (ticketEditor.is_locked && !ticketEditor.access_code.trim()) {
      setTicketValidationError('Access code is required for locked ticket types.');
      setSetupNotice({ kind: 'error', message: 'Access code is required for locked ticket types.' });
      return;
    }
    const normalizedAllowedEmails = normalizeExclusiveEmailsInput(ticketEditor.allowed_emails);
    if (ticketEditor.is_exclusive && normalizedAllowedEmails.length === 0) {
      setTicketValidationError('At least one allowed email is required for exclusive tickets.');
      setSetupNotice({ kind: 'error', message: 'At least one allowed email is required for exclusive tickets.' });
      return;
    }
    if (Number(ticketEditor.per_order_limit || 0) < 1) {
      setTicketValidationError('Per-order limit must be at least 1.');
      setSetupNotice({ kind: 'error', message: 'Per-order limit must be at least 1.' });
      return;
    }
    if (Number(ticketEditor.deposit_amount_cents || 0) > Number(ticketEditor.price_cents || 0)) {
      setTicketValidationError('Deposit cannot be greater than the ticket price.');
      setSetupNotice({ kind: 'error', message: 'Deposit cannot be greater than the ticket price.' });
      return;
    }
    if (ticketEditor.is_sponsor_ticket && ticketEditor.is_group_ticket) {
      setTicketValidationError('Sponsor tickets cannot be configured as group tickets.');
      setSetupNotice({ kind: 'error', message: 'Sponsor tickets cannot be configured as group tickets.' });
      return;
    }
    if (ticketEditor.is_sponsor_ticket && Number(ticketEditor.deposit_amount_cents || 0) > 0) {
      setTicketValidationError('Sponsor tickets require full payment and cannot use deposits.');
      setSetupNotice({ kind: 'error', message: 'Sponsor tickets require full payment and cannot use deposits.' });
      return;
    }
    setTicketValidationError(null);

    const nextTicket: EventTicketType = {
      name: ticketEditor.name.trim(),
      description: ticketEditor.description.trim() || null,
      price_cents: Number(ticketEditor.price_cents || 0),
      deposit_amount_cents: ticketEditor.is_sponsor_ticket
        ? 0
        : Math.min(
          Number(ticketEditor.price_cents || 0),
          Number(ticketEditor.deposit_amount_cents || 0),
        ),
      currency: 'USD',
      is_special: ticketEditor.is_sponsor_ticket ? false : ticketEditor.is_special,
      is_sponsor_ticket: ticketEditor.is_sponsor_ticket,
      is_locked: ticketEditor.is_locked,
      access_code: ticketEditor.is_locked ? ticketEditor.access_code.trim() : null,
      is_exclusive: ticketEditor.is_exclusive,
      exclusive_enforce_once: true,
      allowed_emails: ticketEditor.is_exclusive ? normalizedAllowedEmails : [],
      requires_registration_form: ticketEditor.requires_registration_form,
      is_group_ticket: ticketEditor.is_sponsor_ticket ? false : ticketEditor.is_group_ticket,
      min_group_size: Math.max(1, Number(ticketEditor.min_group_size || 6)),
      capacity: ticketEditor.capacity ? Number(ticketEditor.capacity) : null,
      per_order_limit: ticketEditor.is_sponsor_ticket ? 1 : Number(ticketEditor.per_order_limit || 1),
      status: ticketEditor.status,
      sort_order: editingTicketIndex ?? ticketTypesDraft.length,
      sales_start: salesWindow.sales_start || null,
      sales_end: salesWindow.sales_end || null,
    };

    if (editingTicketIndex === null) {
      setTicketTypesDraft((prev) => [...prev, nextTicket]);
    } else {
      setTicketTypesDraft((prev) =>
        prev.map((ticket, index) => (index === editingTicketIndex ? { ...ticket, ...nextTicket } : ticket))
      );
    }

    startCreateTicket();
  };

  const removeTicketType = (index: number) => {
    setTicketTypesDraft((prev) => prev.filter((_, i) => i !== index).map((row, i) => ({ ...row, sort_order: i })));
    if (editingTicketIndex === index) {
      startCreateTicket();
    }
  };

  const ticketSummary = React.useMemo(() => {
    const totalCapacity = ticketTypesDraft.reduce((sum, ticket) => sum + (ticket.capacity ?? 0), 0);
    const registrations = Number(selectedEvent?.event_registrations?.[0]?.count ?? 0);
    const remaining = Math.max(totalCapacity - registrations, 0);
    const projectedRevenue = ticketTypesDraft.reduce(
      (sum, ticket) => sum + Number(ticket.price_cents ?? 0) * Number(ticket.capacity ?? 0),
      0
    );

    return {
      totalCapacity,
      registrations,
      remaining,
      projectedRevenue,
    };
  }, [selectedEvent?.event_registrations, ticketTypesDraft]);
  const reportingCheckinByRegistrationId = React.useMemo(() => {
    const map = new Map<string, { checkedIn: boolean; lastCheckedInAt: string | null }>();
    for (const row of reportingRegistrationTickets) {
      const registrationId = String(row.registration_id || '').trim();
      if (!registrationId) continue;
      const isCheckedIn = String(row.status || '').toLowerCase() === 'checked_in';
      const current = map.get(registrationId) ?? { checkedIn: false, lastCheckedInAt: null };
      if (isCheckedIn) {
        const nextAt = row.checked_in_at || current.lastCheckedInAt;
        const laterAt =
          nextAt && current.lastCheckedInAt && nextAt < current.lastCheckedInAt ? current.lastCheckedInAt : nextAt;
        map.set(registrationId, { checkedIn: true, lastCheckedInAt: laterAt });
      } else if (!map.has(registrationId)) {
        map.set(registrationId, current);
      }
    }
    return map;
  }, [reportingRegistrationTickets]);
  const reportingTicketTypeLookup = React.useMemo(() => {
    const byId = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const ticketType of reportingTicketTypes) {
      byId.set(ticketType.id, Number(ticketType.price_cents || 0));
      const normalizedName = ticketType.name.trim().toLowerCase();
      if (normalizedName) byName.set(normalizedName, Number(ticketType.price_cents || 0));
    }
    return { byId, byName };
  }, [reportingTicketTypes]);
  const reportingOverview = React.useMemo(() => {
    const rows = reportingRegistrations;
    const confirmedRows = rows.filter((row) => isConfirmedOrderStatus(row.status));
    const totalRegistrations = confirmedRows.length;
    const totalTickets = confirmedRows.reduce((sum, row) => sum + Math.max(1, Number(row.quantity || 1)), 0);
    const checkedInRegistrations = confirmedRows.filter((row) => reportingCheckinByRegistrationId.get(row.id)?.checkedIn).length;
    const showUpRate = totalRegistrations > 0 ? (checkedInRegistrations / totalRegistrations) * 100 : 0;
    const confirmedOrders = confirmedRows.length;
    const confirmedTickets = confirmedRows.reduce((sum, row) => sum + Math.max(1, Number(row.quantity || 1)), 0);
    const grossSales = confirmedRows.reduce((sum, row) => {
      const explicitPaidAmount = Number(row.amount_paid_cents ?? row.total_amount_cents ?? 0);
      if (Number.isFinite(explicitPaidAmount) && explicitPaidAmount > 0) {
        return sum + explicitPaidAmount;
      }
      const quantity = Math.max(1, Number(row.quantity || 1));
      const ticketPriceById = row.ticket_type_id ? reportingTicketTypeLookup.byId.get(row.ticket_type_id) : undefined;
      const ticketPriceByName = reportingTicketTypeLookup.byName.get(
        String(row.ticket_type_name || '').trim().toLowerCase()
      );
      const priceCents = Number(ticketPriceById ?? ticketPriceByName ?? 0);
      return sum + priceCents * quantity;
    }, 0);
    return {
      totalRegistrations,
      totalTickets,
      checkedInRegistrations,
      showUpRate,
      confirmedOrders,
      confirmedTickets,
      grossSales,
    };
  }, [reportingCheckinByRegistrationId, reportingRegistrations, reportingTicketTypeLookup]);
  const reportingTicketTypeBreakdown = React.useMemo(() => {
    const byTicketType = new Map<
      string,
      {
        ticketTypeName: string;
        registrations: number;
        quantity: number;
        grossCents: number;
      }
    >();
    for (const row of reportingRegistrations) {
      if (!isConfirmedOrderStatus(row.status)) continue;
      const ticketTypeName = String(row.ticket_type_name || 'General').trim() || 'General';
      const key = ticketTypeName.toLowerCase();
      const quantity = Math.max(1, Number(row.quantity || 1));
      const ticketPriceById = row.ticket_type_id ? reportingTicketTypeLookup.byId.get(row.ticket_type_id) : undefined;
      const ticketPriceByName = reportingTicketTypeLookup.byName.get(ticketTypeName.toLowerCase());
      const explicitPaidAmount = Number(row.amount_paid_cents ?? row.total_amount_cents ?? 0);
      const estimatedPriceCents = Number(ticketPriceById ?? ticketPriceByName ?? 0);
      const rowGrossCents = Number.isFinite(explicitPaidAmount) && explicitPaidAmount > 0
        ? explicitPaidAmount
        : estimatedPriceCents * quantity;
      const existing = byTicketType.get(key) ?? {
        ticketTypeName,
        registrations: 0,
        quantity: 0,
        grossCents: 0,
      };
      existing.registrations += 1;
      existing.quantity += quantity;
      existing.grossCents += rowGrossCents;
      byTicketType.set(key, existing);
    }
    return Array.from(byTicketType.values()).sort((a, b) => b.quantity - a.quantity);
  }, [reportingRegistrations, reportingTicketTypeLookup]);
  const reportingTicketChangeFinancials = React.useMemo(() => {
    const paidStatuses = new Set(['paid', 'captured', 'completed', 'succeeded']);
    const pendingStatuses = new Set(['pending', 'awaiting_payment', 'requires_payment', 'processing']);
    const statusTotals = new Map<string, number>();
    let upgradeDelta = 0;
    let downgradeDelta = 0;
    let paidDelta = 0;
    let pendingDelta = 0;
    let notRequiredDelta = 0;
    for (const row of reportingTicketChanges) {
      const delta = Number(row.difference_cents || 0);
      const paymentStatus = String(row.payment_status || 'not_required').trim().toLowerCase() || 'not_required';
      if (delta > 0) upgradeDelta += delta;
      if (delta < 0) downgradeDelta += Math.abs(delta);
      statusTotals.set(paymentStatus, (statusTotals.get(paymentStatus) || 0) + delta);
      if (paidStatuses.has(paymentStatus)) paidDelta += delta;
      else if (pendingStatuses.has(paymentStatus)) pendingDelta += delta;
      else if (paymentStatus === 'not_required') notRequiredDelta += delta;
    }
    return {
      upgradeDelta,
      downgradeDelta,
      paidDelta,
      pendingDelta,
      notRequiredDelta,
      statusTotals: Array.from(statusTotals.entries())
        .map(([status, delta]) => ({ status, delta }))
        .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)),
    };
  }, [reportingTicketChanges]);
  const isLoadingReporting =
    isLoadingReportingRegistrations ||
    isLoadingReportingRegistrationTickets ||
    isLoadingReportingTicketTypes ||
    isLoadingReportingTicketChanges;
  const reportingErrorMessage =
    (reportingRegistrationsError as Error | null)?.message ||
    (reportingRegistrationTicketsError as Error | null)?.message ||
    (reportingTicketTypesError as Error | null)?.message ||
    (reportingTicketChangesError as Error | null)?.message ||
    null;

  const validateGeneralDetails = React.useCallback((): ValidationResult => {
    if (!eventDetailsDraft.title.trim() || !eventDetailsDraft.start_date) {
      return { ok: false, message: 'Title and start date are required.' };
    }
    if (eventDetailsDraft.end_date && eventDetailsDraft.end_date < eventDetailsDraft.start_date) {
      return { ok: false, message: 'End date cannot be before start date.' };
    }
    return { ok: true };
  }, [eventDetailsDraft.end_date, eventDetailsDraft.start_date, eventDetailsDraft.title]);

  const addAppointmentSlot = React.useCallback(() => {
    setAppointmentSlotsDraft((prev) => [
      ...prev,
      buildDefaultAppointmentSlotDraft(
        prev.length,
        eventDetailsDraft.start_date,
        eventDetailsDraft.start_time,
        eventDetailsDraft.end_date || eventDetailsDraft.start_date,
        eventDetailsDraft.end_time || eventDetailsDraft.start_time,
      ),
    ]);
  }, [
    eventDetailsDraft.end_date,
    eventDetailsDraft.end_time,
    eventDetailsDraft.start_date,
    eventDetailsDraft.start_time,
  ]);

  const updateAppointmentSlot = React.useCallback(
    (index: number, patch: Partial<AppointmentSlotDraft>) => {
      setAppointmentSlotsDraft((prev) =>
        prev.map((slot, slotIndex) => (slotIndex === index ? { ...slot, ...patch } : slot))
      );
    },
    []
  );

  const removeAppointmentSlot = React.useCallback((index: number) => {
    setAppointmentSlotsDraft((prev) =>
      prev
        .filter((_, slotIndex) => slotIndex !== index)
        .map((slot, slotIndex) => ({ ...slot, sort_order: slotIndex }))
    );
  }, []);

  const validateAppointmentSlots = React.useCallback((): ValidationResult => {
    if (!eventDetailsDraft.appointments_enabled && appointmentSlotsDraft.length === 0) {
      return { ok: true };
    }

    if (eventDetailsDraft.appointments_enabled && appointmentSlotsDraft.length === 0) {
      return { ok: false, message: 'Add at least one appointment slot when appointments are enabled.' };
    }

    const eventStartIso = toIsoFromLocalDateTime(
      eventDetailsDraft.start_date
        ? `${eventDetailsDraft.start_date}T${eventDetailsDraft.start_time || '00:00'}`
        : null
    );
    const eventEndIso = toIsoFromLocalDateTime(
      (eventDetailsDraft.end_date || eventDetailsDraft.start_date)
        ? `${eventDetailsDraft.end_date || eventDetailsDraft.start_date}T${eventDetailsDraft.end_time || '23:59'}`
        : null
    );
    const eventStartMs = eventStartIso ? new Date(eventStartIso).getTime() : Number.NaN;
    const eventEndMs = eventEndIso ? new Date(eventEndIso).getTime() : Number.NaN;

    for (let index = 0; index < appointmentSlotsDraft.length; index += 1) {
      const slot = appointmentSlotsDraft[index];
      const slotNumber = index + 1;
      if (!slot.label.trim()) {
        return { ok: false, message: `Appointment slot #${slotNumber} needs a name.` };
      }
      if (!slot.starts_at || !slot.ends_at) {
        return { ok: false, message: `Appointment slot #${slotNumber} needs a start and end time.` };
      }
      const startIso = toIsoFromLocalDateTime(slot.starts_at);
      const endIso = toIsoFromLocalDateTime(slot.ends_at);
      if (!startIso || !endIso) {
        return { ok: false, message: `Appointment slot #${slotNumber} has an invalid date or time.` };
      }
      const startMs = new Date(startIso).getTime();
      const endMs = new Date(endIso).getTime();
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
        return { ok: false, message: `Appointment slot #${slotNumber} must end after it starts.` };
      }
      if (Math.max(1, Number(slot.capacity || 0)) < 1) {
        return { ok: false, message: `Appointment slot #${slotNumber} needs a capacity of at least 1.` };
      }
      if (Number.isFinite(eventStartMs) && startMs < eventStartMs) {
        return { ok: false, message: `Appointment slot #${slotNumber} starts before the event begins.` };
      }
      if (Number.isFinite(eventEndMs) && endMs > eventEndMs) {
        return { ok: false, message: `Appointment slot #${slotNumber} ends after the event window.` };
      }
    }

    return { ok: true };
  }, [
    appointmentSlotsDraft,
    eventDetailsDraft.appointments_enabled,
    eventDetailsDraft.end_date,
    eventDetailsDraft.end_time,
    eventDetailsDraft.start_date,
    eventDetailsDraft.start_time,
  ]);

  const validateTicketTypes = React.useCallback((): ValidationResult => {
    if (eventDetailsDraft.is_registration_required && ticketTypesDraft.length === 0) {
      return { ok: false, message: 'At least one ticket type is required when registration is enabled.' };
    }
    const hasMissingName = ticketTypesDraft.some((ticketType) => !ticketType.name?.trim());
    if (hasMissingName) {
      return { ok: false, message: 'Each ticket type must have a name.' };
    }
    const hasMissingAccessCode = ticketTypesDraft.some(
      (ticketType) => Boolean(ticketType.is_locked) && !ticketType.access_code?.trim()
    );
    if (hasMissingAccessCode) {
      return { ok: false, message: 'Each locked ticket type must have an access code.' };
    }
    const hasInvalidPerOrderLimit = ticketTypesDraft.some((ticketType) => Number(ticketType.per_order_limit ?? 0) < 1);
    if (hasInvalidPerOrderLimit) {
      return { ok: false, message: 'Each ticket type needs a per-order limit of at least 1.' };
    }
    const hasInvalidDepositAmount = ticketTypesDraft.some(
      (ticketType) => Number(ticketType.deposit_amount_cents ?? 0) > Number(ticketType.price_cents ?? 0)
    );
    if (hasInvalidDepositAmount) {
      return { ok: false, message: 'Ticket deposits cannot be greater than the ticket price.' };
    }
    const hasMissingExclusiveAllowlist = ticketTypesDraft.some(
      (ticketType) =>
        Boolean(ticketType.is_exclusive) &&
        (!Array.isArray(ticketType.allowed_emails) || ticketType.allowed_emails.length === 0)
    );
    if (hasMissingExclusiveAllowlist) {
      return { ok: false, message: 'Each exclusive ticket must include at least one allowed email.' };
    }
    return { ok: true };
  }, [eventDetailsDraft.is_registration_required, ticketTypesDraft]);

  const validateOrderForm = React.useCallback(
    (fields: EventOrderFormField[]): ValidationResult => {
      const hasMissingLabel = fields.some((field) => !field.label?.trim());
      if (hasMissingLabel) return { ok: false, message: 'Each form field must have a label.' };
      return { ok: true };
    },
    []
  );

  const saveTicketTypeChanges = async (options?: { silent?: boolean; throwOnError?: boolean }) => {
    if (!eventId) return;
    const validation = validateTicketTypes();
    if (!validation.ok) {
      const message = validation.message ?? 'Ticket types are invalid.';
      setTicketValidationError(message);
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw new Error(message);
      return false;
    }

    try {
      setTicketValidationError(null);
      await saveTicketTypes(
        ticketTypesDraft.map((ticketType, index) => ({
          ...ticketType,
          name: ticketType.name.trim(),
          description: ticketType.description?.trim() || null,
          is_special: Boolean(ticketType.is_special),
          is_sponsor_ticket: Boolean(ticketType.is_sponsor_ticket),
          is_locked: Boolean(ticketType.is_locked),
          access_code: ticketType.is_locked ? ticketType.access_code?.trim() || null : null,
          is_exclusive: Boolean(ticketType.is_exclusive),
          exclusive_enforce_once: Boolean(ticketType.exclusive_enforce_once ?? true),
          allowed_emails: ticketType.is_exclusive
            ? normalizeExclusiveEmailList(ticketType.allowed_emails)
            : [],
          requires_registration_form: ticketType.requires_registration_form !== false,
          is_group_ticket: Boolean(ticketType.is_group_ticket),
          min_group_size: Math.max(1, Number(ticketType.min_group_size ?? 6)),
          sort_order: index,
          price_cents: Number(ticketType.price_cents ?? 0),
          deposit_amount_cents: ticketType.is_sponsor_ticket
            ? 0
            : Math.min(
              Number(ticketType.price_cents ?? 0),
              Number(ticketType.deposit_amount_cents ?? 0),
            ),
          per_order_limit: ticketType.is_sponsor_ticket ? 1 : Number(ticketType.per_order_limit ?? 10),
          capacity:
            ticketType.capacity === null || ticketType.capacity === undefined
              ? null
              : Number(ticketType.capacity),
        }))
      );
      if (!options?.silent) {
        setSetupNotice({ kind: 'success', message: 'Ticket types saved.' });
      }
      return true;
    } catch (err) {
      console.error(err);
      const message =
        err instanceof Error && err.message
          ? `Could not save ticket types: ${err.message}`
          : 'Could not save ticket types. Please try again.';
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw err;
      return false;
    }
  };

  const addFormField = (group: 'registration' | 'waiver' = 'registration') => {
    setOrderFormDraft((prev) => ({
      ...prev,
      fields: [
        ...(prev.fields ?? []),
        {
          field_key: `${group}_${Date.now()}`,
          label: '',
          field_type: 'short_text',
          is_required: false,
          field_group: group,
          sort_order: prev.fields?.length ?? 0,
          options: [],
          validation: {},
          visibility_rules: {},
          placeholder: '',
          help_text: '',
        },
      ],
    }));
  };

  const getFieldUiKey = (field: EventOrderFormField, index: number) =>
    `${field.field_key?.trim() || 'field'}_${index}`;

  const isFieldCollapsedByDefault = (field: EventOrderFormField, index: number) => {
    const uiKey = getFieldUiKey(field, index);
    return collapsedFields[uiKey] ?? true;
  };

  const toggleFieldCollapse = (field: EventOrderFormField, index: number) => {
    const uiKey = getFieldUiKey(field, index);
    setCollapsedFields((prev) => {
      const currentState = prev[uiKey] ?? true;
      return { ...prev, [uiKey]: !currentState };
    });
  };

  const updateFormField = (index: number, patch: Partial<EventOrderFormField>) => {
    setOrderFormDraft((prev) => ({
      ...prev,
      fields: (prev.fields ?? []).map((field, i) => {
        if (i !== index) return field;
        if (isDefaultOrderField(field)) {
          const patchKeys = Object.keys(patch);
          const isRequiredOnlyPatch = patchKeys.length === 1 && patchKeys[0] === 'is_required';
          if (!isRequiredOnlyPatch) return field;
        }
        return { ...field, ...patch };
      }),
    }));
  };

  const removeFormField = (index: number) => {
    setOrderFormDraft((prev) => {
      const targetField = prev.fields?.[index];
      if (!targetField || isDefaultOrderField(targetField)) return prev;

      return {
        ...prev,
        fields: (prev.fields ?? []).filter((_, i) => i !== index).map((field, i) => ({ ...field, sort_order: i })),
      };
    });
  };

  const moveFormField = (index: number, direction: 'up' | 'down') => {
    setOrderFormDraft((prev) => {
      const fields = [...(prev.fields ?? [])];
      const group = fieldGroupOf(fields[index] ?? {});
      const groupIndexes = fields
        .map((field, fieldIndex) => (fieldGroupOf(field) === group ? fieldIndex : -1))
        .filter((fieldIndex) => fieldIndex >= 0);
      const position = groupIndexes.indexOf(index);
      const targetIndex = groupIndexes[position + (direction === 'up' ? -1 : 1)];
      if (position < 0 || targetIndex === undefined || !fields[index]) return prev;

      const current = fields[index];
      fields[index] = fields[targetIndex];
      fields[targetIndex] = current;

      return {
        ...prev,
        fields: fields.map((field, sortIndex) => ({ ...field, sort_order: sortIndex })),
      };
    });
  };

  const saveOrderFormChanges = async (options?: { throwOnError?: boolean; silent?: boolean }) => {
    if (!eventId) return;

    const normalizedFields = enforceDefaultOrderFields(orderFormDraft.fields ?? []);
    const validation = validateOrderForm(normalizedFields);
    if (!validation.ok) {
      const message = validation.message ?? 'Order form is invalid.';
      setFormValidationError(message);
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw new Error(message);
      return false;
    }

    try {
      setFormPayloadError(null);
      setFormValidationError(null);
      setOrderFormDraft((prev) => ({ ...prev, fields: normalizedFields }));
      await saveOrderForm({
        ...orderFormDraft,
        is_enabled: !!orderFormDraft.is_enabled,
        waiver_enabled: orderFormDraft.waiver_enabled === true,
        fields: normalizedFields.map((field, index) => ({
          ...field,
          field_group: fieldGroupOf(field),
          field_key:
            field.field_key?.trim() ||
            `field_${field.label
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '_')
              .replace(/^_+|_+$/g, '') || index}`,
          label: field.label.trim(),
          sort_order: index,
          options: field.options ?? [],
          validation: field.validation ?? {},
          visibility_rules: field.visibility_rules ?? {},
        })),
      });
      if (!options?.silent) {
        setSetupNotice({
          kind: 'success',
          message: activeTab === 'waiver' ? 'Waiver saved.' : 'Order form saved.',
        });
      }
      return true;
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Could not save order form.';
      setFormPayloadError(message);
      setFormValidationError(message);
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw err;
      return false;
    }
  };

  const saveAppointmentSlotChanges = async (options?: { throwOnError?: boolean; silent?: boolean }) => {
    if (!eventId) return false;

    const validation = validateAppointmentSlots();
    if (!validation.ok) {
      const message = validation.message ?? 'Appointments are invalid.';
      setAppointmentValidationError(message);
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw new Error(message);
      return false;
    }

    try {
      setAppointmentValidationError(null);
      await saveAppointmentSlots(
        appointmentSlotsDraft.map((slot, index) => {
          const startsAtIso = toIsoFromLocalDateTime(slot.starts_at);
          const endsAtIso = toIsoFromLocalDateTime(slot.ends_at);
          if (!startsAtIso || !endsAtIso) {
            throw new Error(`Appointment slot "${slot.label || `#${index + 1}`}" has an invalid date/time.`);
          }
          return {
            id: slot.id,
            label: slot.label.trim(),
            starts_at: startsAtIso,
            ends_at: endsAtIso,
            capacity: Math.max(1, Number(slot.capacity || 1)),
            status: slot.status === 'inactive' ? 'inactive' : 'active',
            sort_order: index,
          };
        })
      );
      if (!options?.silent) {
        setSetupNotice({ kind: 'success', message: 'Appointment settings saved.' });
      }
      return true;
    } catch (err) {
      console.error(err);
      const message = err instanceof Error ? err.message : 'Could not save appointment slots.';
      setAppointmentValidationError(message);
      setSetupNotice({ kind: 'error', message });
      if (options?.throwOnError) throw err;
      return false;
    }
  };

  const saveEventDetails = async (statusOverride?: string) => {
    if (!eventId) return;
    const validation = validateGeneralDetails();
    if (!validation.ok) {
      const message = validation.message ?? 'General details are invalid.';
      setGeneralValidationError(message);
      setSetupNotice({ kind: 'error', message });
      throw new Error(message);
    }

    await updateEvent({
      eventId,
      title: eventDetailsDraft.title.trim(),
      description: eventDetailsDraft.description.trim() || null,
      start_date: eventDetailsDraft.start_date,
      end_date: eventDetailsDraft.end_date || eventDetailsDraft.start_date,
      start_time: eventDetailsDraft.start_time || null,
      end_time: eventDetailsDraft.end_time || eventDetailsDraft.start_time || null,
      image_url: getPrimaryImageUrl(eventDetailsDraft.media_items, eventDetailsDraft.image_url.trim() || null),
      media_items: eventDetailsDraft.media_items,
      location: eventDetailsDraft.location.trim() || null,
      ministry: eventDetailsDraft.ministry.trim() || null,
      category: normalizeEventType(eventDetailsDraft.category),
      status: statusOverride ?? eventDetailsDraft.status,
      is_registration_required: eventDetailsDraft.is_registration_required,
      hide_registration_details_when_no_public_tickets:
        eventDetailsDraft.hide_registration_details_when_no_public_tickets,
      appointments_enabled: eventDetailsDraft.appointments_enabled,
      appointment_booking_mode: eventDetailsDraft.appointment_booking_mode,
      paypal_account: eventDetailsDraft.paypal_account,
    });
    await saveImhimWebsiteLink({ eventId, enabled: linkToImhimWebsite });
  };

  const handleSaveEventDetails = async () => {
    try {
      setGeneralValidationError(null);
      await saveEventDetails();
      setSetupNotice({ kind: 'success', message: 'Event details saved.' });
    } catch (err) {
      console.error(err);
      if (!(err instanceof Error)) {
        setSetupNotice({ kind: 'error', message: 'Could not save event details. Please try again.' });
      }
    }
  };

  const handleSaveAppointmentSettings = async () => {
    try {
      setAppointmentValidationError(null);
      await saveAppointmentSlotChanges({ silent: true, throwOnError: true });
      await saveEventDetails();
      setSetupNotice({ kind: 'success', message: 'Appointment settings saved.' });
    } catch (err) {
      console.error(err);
      if (!(err instanceof Error)) {
        setSetupNotice({ kind: 'error', message: 'Could not save appointment settings. Please try again.' });
      }
    }
  };

  const approveLinkedEventRequest = async () => {
    if (!eventId) return;

    const { data, error } = await supabase
      .from('event_requests')
      .select('id, status')
      .eq('event_id', eventId)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!data || data.status === 'approved') return;

    await updateEventRequest({ id: data.id, status: 'approved' });
  };

  const hasTicketValidationIssues = React.useMemo(() => !validateTicketTypes().ok, [validateTicketTypes]);

  const handlePublishFromSetup = async () => {
    try {
      setSetupNotice(null);
      setGeneralValidationError(null);
      setTicketValidationError(null);
      setFormValidationError(null);
      setAppointmentValidationError(null);
      if (isLoadingTicketTypes) {
        setSetupNotice({ kind: 'info', message: 'Ticket types are still loading. Please wait and try again.' });
        return;
      }
      const normalizedFields = enforceDefaultOrderFields(orderFormDraft.fields ?? []);
      const formValidation = validateOrderForm(normalizedFields);
      if (!formValidation.ok) {
        const message = formValidation.message ?? 'Form fields are invalid.';
        setFormValidationError(message);
        setSetupNotice({ kind: 'error', message: `Publish blocked: ${message}` });
        return;
      }
      setOrderFormDraft((prev) => ({ ...prev, fields: normalizedFields }));
      const ticketSaved = await saveTicketTypeChanges({ silent: true, throwOnError: true });
      if (!ticketSaved) return;
      const appointmentsSaved = await saveAppointmentSlotChanges({ silent: true, throwOnError: true });
      if (!appointmentsSaved) return;
      const formSaved = await saveOrderFormChanges({ silent: true, throwOnError: true });
      if (!formSaved) return;
      await saveEventDetails('published');
      await approveLinkedEventRequest();
      setEventDetailsDraft((prev) => ({ ...prev, status: 'published' }));
      setSetupNotice({ kind: 'success', message: 'Event published successfully.' });
      setIsPublishQrModalOpen(true);
    } catch (err) {
      console.error(err);
      if (err instanceof Error) {
        setSetupNotice({ kind: 'error', message: `Publish blocked: ${err.message}` });
      } else {
        setSetupNotice({ kind: 'error', message: 'Could not publish event. Please try again.' });
      }
    }
  };

  const handleUnpublishFromSetup = async () => {
    try {
      await saveEventDetails('draft');
      setEventDetailsDraft((prev) => ({ ...prev, status: 'draft' }));
      setIsPublishQrModalOpen(false);
      setSetupNotice({ kind: 'success', message: 'Event unpublished.' });
    } catch (err) {
      console.error(err);
      setSetupNotice({ kind: 'error', message: 'Could not unpublish event. Please try again.' });
    }
  };

  const copyTextToClipboard = React.useCallback(async (value: string) => {
    if (!value) return false;
    try {
      if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = value;
        textArea.setAttribute('readonly', 'true');
        textArea.style.position = 'absolute';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  }, []);

  const handleCopyRegistrationLink = async () => {
    if (!websiteEventUrl) return;
    const copied = await copyTextToClipboard(websiteEventUrl);
    if (copied) {
      setSetupNotice({ kind: 'success', message: 'Registration link copied.' });
    } else {
      setSetupNotice({ kind: 'error', message: 'Could not copy the registration link.' });
    }
  };

  const handleOpenRegistrationLink = () => {
    if (!websiteEventUrl) return;
    window.open(websiteEventUrl, '_blank', 'noopener,noreferrer');
  };

  const buildTicketRegistrationUrl = React.useCallback(
    (ticketTypeId?: string | null) => {
      if (!eventId) return '';
      const normalizedTicketTypeId = String(ticketTypeId || '').trim();
      if (!normalizedTicketTypeId) return '';
      return buildWebsiteTicketRegistrationUrl(eventId, normalizedTicketTypeId);
    },
    [eventId]
  );

  const handleCopyTicketRegistrationLink = React.useCallback(
    async (ticketTypeId?: string | null) => {
      const ticketUrl = buildTicketRegistrationUrl(ticketTypeId);
      if (!ticketUrl) {
        setSetupNotice({ kind: 'error', message: 'Could not build special ticket link.' });
        return;
      }
      const copied = await copyTextToClipboard(ticketUrl);
      if (copied) {
        setSetupNotice({ kind: 'success', message: 'Special ticket link copied.' });
      } else {
        setSetupNotice({ kind: 'error', message: 'Could not copy the special ticket link.' });
      }
    },
    [buildTicketRegistrationUrl, copyTextToClipboard]
  );

  const handleOpenTicketRegistrationLink = React.useCallback(
    (ticketTypeId?: string | null) => {
      const ticketUrl = buildTicketRegistrationUrl(ticketTypeId);
      if (!ticketUrl) {
        setSetupNotice({ kind: 'error', message: 'Could not build special ticket link.' });
        return;
      }
      window.open(ticketUrl, '_blank', 'noopener,noreferrer');
    },
    [buildTicketRegistrationUrl]
  );

  const getRenderedQrNode = () => {
    const isRenderableQrNode = (node: Element): node is HTMLCanvasElement | SVGElement | HTMLImageElement =>
      node instanceof HTMLCanvasElement || node instanceof SVGElement || node instanceof HTMLImageElement;

    const findVisibleQrNode = (container: HTMLDivElement | null) => {
      if (!container) return null;
      const candidates = Array.from(container.querySelectorAll('img,canvas,svg')).filter(isRenderableQrNode);
      for (const candidate of candidates) {
        const styles = window.getComputedStyle(candidate);
        if (styles.display === 'none' || styles.visibility === 'hidden') continue;
        const rect = candidate.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;
        return candidate;
      }
      return null;
    };

    const modalNode = findVisibleQrNode(modalQrContainerRef.current);
    if (modalNode) return modalNode;
    return findVisibleQrNode(persistentQrContainerRef.current);
  };

  const handleDownloadQrImage = async () => {
    const qrNode = getRenderedQrNode();
    if (!qrNode) {
      setSetupNotice({ kind: 'error', message: 'Could not find QR image to download. Please try again.' });
      return;
    }

    const outputSize = EVENT_QR_DOWNLOAD_SIZE;
    const padding = EVENT_QR_DOWNLOAD_INSET;
    const qrSize = outputSize - (padding * 2);
    const outputCanvas = document.createElement('canvas');
    outputCanvas.width = outputSize;
    outputCanvas.height = outputSize;
    const ctx = outputCanvas.getContext('2d');
    if (!ctx) {
      setSetupNotice({ kind: 'error', message: 'Could not prepare QR download image.' });
      return;
    }

    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, outputSize, outputSize);
    ctx.imageSmoothingEnabled = false;

    if (qrNode instanceof HTMLCanvasElement) {
      ctx.drawImage(qrNode, padding, padding, qrSize, qrSize);
    } else if (qrNode instanceof HTMLImageElement) {
      if (!qrNode.complete || qrNode.naturalWidth === 0) {
        try {
          await qrNode.decode();
        } catch (err) {
          console.error(err);
          setSetupNotice({ kind: 'error', message: 'Could not decode QR image for download.' });
          return;
        }
      }
      ctx.drawImage(qrNode, padding, padding, qrSize, qrSize);
    } else if (qrNode instanceof SVGElement) {
      const svgMarkup = new XMLSerializer().serializeToString(qrNode);
      const svgBlob = new Blob([svgMarkup], { type: 'image/svg+xml;charset=utf-8' });
      const svgObjectUrl = URL.createObjectURL(svgBlob);
      try {
        await new Promise<void>((resolve, reject) => {
          const image = new Image();
          image.onload = () => {
            ctx.drawImage(image, padding, padding, qrSize, qrSize);
            resolve();
          };
          image.onerror = () => reject(new Error('Failed to load QR SVG'));
          image.src = svgObjectUrl;
        });
      } catch (err) {
        console.error(err);
        setSetupNotice({ kind: 'error', message: 'Could not convert QR image for download.' });
        return;
      } finally {
        URL.revokeObjectURL(svgObjectUrl);
      }
    } else {
      setSetupNotice({ kind: 'error', message: 'Unsupported QR image format.' });
      return;
    }

    const downloadLink = document.createElement('a');
    const idPart = (eventId || 'event').replace(/[^a-z0-9_-]+/gi, '-');
    downloadLink.download = `${idPart}-qr-${EVENT_QR_DOWNLOAD_SIZE}.png`;
    downloadLink.href = outputCanvas.toDataURL('image/png');
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const uploadBannerImage = async (file: File) => {
    const ext = file.name.split('.').pop() || 'jpg';
    const fileName = `event-banners/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const { error } = await supabase.storage.from('post-images').upload(fileName, file, {
      upsert: false,
    });
    if (error) throw error;
    const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const handleBannerUpload = async (file?: File) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setSetupNotice({ kind: 'error', message: 'Please upload an image smaller than 5MB.' });
      return;
    }
    if (!file.type.startsWith('image/')) {
      setSetupNotice({ kind: 'error', message: 'Please upload a valid image file.' });
      return;
    }

    setIsUploadingBanner(true);
    try {
      const imageUrl = await uploadBannerImage(file);
      setEventDetailsDraft((prev) => {
        const nextItems = [
          ...prev.media_items,
          { type: 'image' as const, url: imageUrl, position: prev.media_items.length, thumbnail_url: null },
        ];
        return {
          ...prev,
          image_url: getPrimaryImageUrl(nextItems, imageUrl) ?? '',
          media_items: nextItems,
        };
      });
      setSetupNotice({ kind: 'success', message: 'Banner image uploaded.' });
    } catch (err) {
      console.error(err);
      setSetupNotice({ kind: 'error', message: 'Could not upload image. Please try again.' });
    } finally {
      setIsUploadingBanner(false);
    }
  };

  const handleAddVideoMedia = React.useCallback((url: string) => {
    const normalized = url.trim();
    if (!normalized) return;
    setEventDetailsDraft((prev) => {
      const nextItems = [
        ...prev.media_items,
        { type: 'video' as const, url: normalized, position: prev.media_items.length, thumbnail_url: null },
      ];
      return {
        ...prev,
        media_items: nextItems,
      };
    });
  }, []);

  const handleMoveMediaItem = React.useCallback((index: number, direction: -1 | 1) => {
    setEventDetailsDraft((prev) => {
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= prev.media_items.length) return prev;
      const nextItems = [...prev.media_items];
      const temp = nextItems[index];
      nextItems[index] = nextItems[targetIndex];
      nextItems[targetIndex] = temp;
      return {
        ...prev,
        image_url: getPrimaryImageUrl(nextItems, prev.image_url) ?? '',
        media_items: nextItems.map((item, idx) => ({ ...item, position: idx })),
      };
    });
  }, []);

  const handleRemoveMediaItem = React.useCallback((index: number) => {
    setEventDetailsDraft((prev) => {
      const nextItems = prev.media_items.filter((_, idx) => idx !== index).map((item, idx) => ({ ...item, position: idx }));
      return {
        ...prev,
        image_url: getPrimaryImageUrl(nextItems, null) ?? '',
        media_items: nextItems,
      };
    });
  }, []);

  if (!eventId) {
    return <Page>Missing event id.</Page>;
  }

  if (isLoadingEvents) {
    return <Page>Loading event setup…</Page>;
  }

  if (eventsError || !selectedEvent) {
    return (
      <Page>
      <SectionHeader>
        <PageTitle>Event Setup</PageTitle>
        <HeaderActions>
          <SecondaryActionButton type="button" onClick={handleOpenSendEmailPage}>
            Send Email
          </SecondaryActionButton>
          <SecondaryActionButton type="button" onClick={() => navigate('/events')}>
            Back to Events
          </SecondaryActionButton>
        </HeaderActions>
      </SectionHeader>
        <EmptyState>{eventsError ? 'Could not load this event right now.' : 'Event not found.'}</EmptyState>
      </Page>
    );
  }

  return (
    <Page>
      <SectionHeader>
        <PageTitle>Event Setup</PageTitle>
        <HeaderActions>
          <SecondaryActionButton type="button" onClick={handleOpenSendEmailPage}>
            Send Email
          </SecondaryActionButton>
          <SecondaryActionButton type="button" onClick={() => navigate('/events')}>
            Back to Events
          </SecondaryActionButton>
        </HeaderActions>
      </SectionHeader>
      {setupNotice ? <InlineNotice $kind={setupNotice.kind}>{setupNotice.message}</InlineNotice> : null}

      <TabsRow>
        <TabButton
          type="button"
          data-active={activeTab === 'general'}
          onClick={() => handleTabChange('general')}
        >
          General Info
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'appointments'}
          onClick={() => handleTabChange('appointments')}
        >
          Appointments
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'ticketing'}
          onClick={() => handleTabChange('ticketing')}
        >
          Ticketing
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'form'}
          onClick={() => handleTabChange('form')}
        >
          Form
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'waiver'}
          onClick={() => handleTabChange('waiver')}
        >
          Waiver
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'waiver-list'}
          onClick={() => handleTabChange('waiver-list')}
        >
          Waiver List
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'registrants'}
          onClick={() => handleTabChange('registrants')}
        >
          Registrants
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'tshirts'}
          onClick={() => handleTabChange('tshirts')}
        >
          T-shirts
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'communication'}
          onClick={() => handleTabChange('communication')}
        >
          Communication History
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'reporting'}
          onClick={() => handleTabChange('reporting')}
        >
          Reporting
        </TabButton>
        <TabButton
          type="button"
          data-active={activeTab === 'admins'}
          onClick={() => handleTabChange('admins')}
        >
          Event Admins
        </TabButton>
      </TabsRow>

      <ManageGrid>
        {activeTab === 'general' ? (
          <GeneralInfoCard>
            <GeneralSectionTitle>
              <StatusDot aria-hidden="true">●</StatusDot>
              General Information
            </GeneralSectionTitle>
            {generalValidationError ? <ErrorText>{generalValidationError}</ErrorText> : null}

            <Field>
              <label>Event Title</label>
              <input
                value={eventDetailsDraft.title}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({ ...prev, title: event.target.value }))
                }
              />
            </Field>

            <GeneralTopGrid>
              <Field>
                <label>Start Date</label>
                <input
                  type="date"
                  value={eventDetailsDraft.start_date}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, start_date: event.target.value }))
                  }
                />
              </Field>
              <Field>
                <label>Start Time</label>
                <input
                  type="time"
                  value={eventDetailsDraft.start_time}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, start_time: event.target.value }))
                  }
                />
              </Field>
              <Field>
                <label>End Date</label>
                <input
                  type="date"
                  value={eventDetailsDraft.end_date}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, end_date: event.target.value }))
                  }
                />
              </Field>
              <Field>
                <label>End Time</label>
                <input
                  type="time"
                  value={eventDetailsDraft.end_time}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, end_time: event.target.value }))
                  }
                />
              </Field>
            </GeneralTopGrid>

            <GeneralMetaGrid>
              <Field>
                <label>Status</label>
                <select
                  value={eventDetailsDraft.status}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, status: event.target.value }))
                  }
                >
                  <option value="draft">Draft</option>
                  <option value="approved">Approved</option>
                  <option value="published">Published</option>
                </select>
              </Field>

              <Field>
                <label>Location</label>
                <input
                  value={eventDetailsDraft.location}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, location: event.target.value }))
                  }
                />
              </Field>
              <Field>
                <label htmlFor="event-ministry">Ministry</label>
                <MinistryDropdownContainer>
                  <input
                    id="event-ministry"
                    value={eventDetailsDraft.ministry}
                    placeholder="Select ministry/department"
                    onChange={(event) =>
                      setEventDetailsDraft((prev) => ({ ...prev, ministry: event.target.value }))
                    }
                    onFocus={() => setIsMinistryDropdownOpen(true)}
                    onBlur={() => {
                      window.setTimeout(() => setIsMinistryDropdownOpen(false), 120);
                    }}
                  />
                  {isMinistryDropdownOpen ? (
                    <MinistryDropdownList>
                      {filteredMinistryOptions.length > 0 ? (
                        filteredMinistryOptions.map((label) => (
                          <MinistryDropdownItem
                            key={label}
                            onMouseDown={() => {
                              setEventDetailsDraft((prev) => ({ ...prev, ministry: label }));
                              setIsMinistryDropdownOpen(false);
                            }}
                          >
                            {label}
                          </MinistryDropdownItem>
                        ))
                      ) : (
                        <MinistryDropdownEmpty>No matches found.</MinistryDropdownEmpty>
                      )}
                    </MinistryDropdownList>
                  ) : null}
                </MinistryDropdownContainer>
              </Field>
              <Field>
                <label>Event Type</label>
                <select
                  value={eventDetailsDraft.category}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({ ...prev, category: event.target.value }))
                  }
                >
                  <option value="Event">Event</option>
                  <option value="General Announcement">General Announcement</option>
                </select>
              </Field>
              <Field>
                <label>PayPal Account</label>
                <select
                  value={eventDetailsDraft.paypal_account}
                  onChange={(event) =>
                    setEventDetailsDraft((prev) => ({
                      ...prev,
                      paypal_account:
                        event.target.value === 'event_paypal'
                          ? 'event_paypal'
                          : event.target.value === 'none'
                            ? 'none'
                            : 'ncof',
                    }))
                  }
                >
                  <option value="ncof">PayPal 1 (NCOF)</option>
                  <option value="event_paypal">Event PayPal (BETTER)</option>
                  <option value="none">None</option>
                </select>
              </Field>
            </GeneralMetaGrid>

            <SwitchRow>
              <label htmlFor="event-registration-required">Enable event registration</label>
              <input
                id="event-registration-required"
                type="checkbox"
                checked={!!eventDetailsDraft.is_registration_required}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({ ...prev, is_registration_required: event.target.checked }))
                }
              />
            </SwitchRow>
            <Hint>
              Turn this on only for events that should show a Register/Get Tickets button in the mobile app and on the
              website.
            </Hint>

            <SwitchRow>
              <label htmlFor="event-imhim-website-link">Use this event for the IMHIM website Get Ticket form</label>
              <input
                id="event-imhim-website-link"
                type="checkbox"
                checked={linkToImhimWebsite}
                onChange={(event) => setLinkToImhimWebsite(event.target.checked)}
              />
            </SwitchRow>
            <Hint>
              Only one event can be linked. Enabling this event replaces the previous IMHIM website event. Registrants
              and t-shirts from that site land under this event.
              {imhimWebsiteLink?.event_id && imhimWebsiteLink.event_id !== eventId
                ? ` Currently linked: ${imhimWebsiteLink.event_title || 'another event'}.`
                : ''}
            </Hint>

            <SwitchRow>
              <label htmlFor="event-hide-registration-details-no-public">
                Hide website registration details when only private tickets are available
              </label>
              <input
                id="event-hide-registration-details-no-public"
                type="checkbox"
                checked={!!eventDetailsDraft.hide_registration_details_when_no_public_tickets}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({
                    ...prev,
                    hide_registration_details_when_no_public_tickets: event.target.checked,
                  }))
                }
                disabled={!eventDetailsDraft.is_registration_required}
              />
            </SwitchRow>
            <Hint>
              When this is on, the website hides the registration form until a public ticket is available or someone
              opens a private ticket with an access code or special link.
            </Hint>

            <Field>
              <label>Description</label>
              <textarea
                rows={3}
                value={eventDetailsDraft.description}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({ ...prev, description: event.target.value }))
                }
              />
            </Field>

            <Field>
              <label>Event Media (optional)</label>
            </Field>
            <BannerPreview style={{ backgroundImage: eventDetailsDraft.image_url ? `url(${eventDetailsDraft.image_url})` : undefined }}>
              <BannerOverlayLabel>
                {isUploadingBanner ? 'Uploading…' : 'Primary Cover Preview'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => handleBannerUpload(event.target.files?.[0])}
                  disabled={isUploadingBanner}
                />
              </BannerOverlayLabel>
            </BannerPreview>
            <Hint>Add images and external video URLs. One item renders directly; multiple items render as a slider.</Hint>
            <EventMediaEditor
              mediaItems={eventDetailsDraft.media_items}
              isUploading={isUploadingBanner}
              onUploadImage={handleBannerUpload}
              onAddVideoUrl={handleAddVideoMedia}
              onMoveItem={handleMoveMediaItem}
              onRemoveItem={handleRemoveMediaItem}
            />

            <ButtonRow>
              <SecondaryActionButton
                type="button"
                onClick={handleSaveEventDetails}
                disabled={isSavingEventDetails}
              >
                {isSavingEventDetails ? 'Saving…' : 'Save Details'}
              </SecondaryActionButton>
              <ActionButton
                type="button"
                onClick={eventDetailsDraft.status === 'published' ? handleUnpublishFromSetup : handlePublishFromSetup}
                disabled={
                  isSavingEventDetails ||
                  isSavingAppointmentSlots ||
                  isSavingTicketTypes ||
                  isSavingOrderForm ||
                  (eventDetailsDraft.status !== 'published' && hasTicketValidationIssues)
                }
              >
                {isSavingEventDetails
                  ? 'Working…'
                  : eventDetailsDraft.status === 'published'
                    ? 'Unpublish Event'
                    : 'Publish Event'}
              </ActionButton>
            </ButtonRow>
            {hasTicketValidationIssues ? (
              <Hint>Publishing is disabled until ticketing issues are resolved.</Hint>
            ) : null}

            {eventDetailsDraft.status === 'published' && websiteEventUrl ? (
              <RegistrationQrCard>
                <ManageCardHeader>
                  <h3>Registration QR</h3>
                </ManageCardHeader>
                <RegistrationQrLayout>
                  <RegistrationQrBlock ref={persistentQrContainerRef}>
                    <QRCode
                      value={websiteEventUrl}
                      size={168}
                      type="svg"
                      color={EVENT_QR_FOREGROUND}
                      bgColor={EVENT_QR_BACKGROUND}
                    />
                  </RegistrationQrBlock>
                  <RegistrationQrContent>
                    <RegistrationQrHint>
                      Scan this code to open the website event registration page directly.
                    </RegistrationQrHint>
                    <RegistrationQrLink
                      href={websiteEventUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {websiteEventUrl}
                    </RegistrationQrLink>
                    <RegistrationQrActions>
                      <SecondaryActionButton type="button" onClick={handleCopyRegistrationLink}>
                        Copy Link
                      </SecondaryActionButton>
                      <SecondaryActionButton type="button" onClick={handleDownloadQrImage}>
                        Download 2048x2048
                      </SecondaryActionButton>
                      <ActionButton type="button" onClick={handleOpenRegistrationLink}>
                        Open Link
                      </ActionButton>
                    </RegistrationQrActions>
                  </RegistrationQrContent>
                </RegistrationQrLayout>
              </RegistrationQrCard>
            ) : null}
          </GeneralInfoCard>
        ) : null}

        {activeTab === 'appointments' ? (
          <AppointmentsTabCard>
            <GeneralSectionTitle>
              <StatusDot aria-hidden="true">●</StatusDot>
              Appointment Booking
            </GeneralSectionTitle>
            {appointmentValidationError ? <ErrorText>{appointmentValidationError}</ErrorText> : null}
            <SwitchRow>
              <label htmlFor="event-appointments-enabled">Enable appointments for this event</label>
              <input
                id="event-appointments-enabled"
                type="checkbox"
                checked={!!eventDetailsDraft.appointments_enabled}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({ ...prev, appointments_enabled: event.target.checked }))
                }
              />
            </SwitchRow>
            <Hint>
              Appointments are optional for registrants. When enabled, the website and mobile registration flows will
              let people choose one or more time slots inside this event.
            </Hint>

            <Field>
              <label>Booking mode</label>
              <select
                value={eventDetailsDraft.appointment_booking_mode}
                onChange={(event) =>
                  setEventDetailsDraft((prev) => ({
                    ...prev,
                    appointment_booking_mode: event.target.value === 'multiple' ? 'multiple' : 'single',
                  }))
                }
                disabled={!eventDetailsDraft.appointments_enabled}
              >
                <option value="single">Registrants may choose one slot</option>
                <option value="multiple">Registrants may choose multiple slots</option>
              </select>
            </Field>

            <AppointmentSlotsList>
              {appointmentSlotsDraft.map((slot, index) => (
                <AppointmentSlotCard key={slot.id || `appointment-slot-${index}`}>
                  <AppointmentSlotHeader>
                    <strong>Slot {index + 1}</strong>
                    <InlineDangerButton type="button" onClick={() => removeAppointmentSlot(index)}>
                      Remove
                    </InlineDangerButton>
                  </AppointmentSlotHeader>
                  <AppointmentSlotGrid>
                    <Field>
                      <label>Appointment name</label>
                      <input
                        value={slot.label}
                        onChange={(event) => updateAppointmentSlot(index, { label: event.target.value })}
                        placeholder="Prayer meeting, photo time, interview, etc."
                      />
                    </Field>
                    <Field>
                      <label>Capacity</label>
                      <input
                        type="number"
                        min={1}
                        value={slot.capacity}
                        onChange={(event) =>
                          updateAppointmentSlot(index, { capacity: Math.max(1, Number(event.target.value || 1)) })
                        }
                      />
                    </Field>
                    <Field>
                      <label>Start</label>
                      <input
                        type="datetime-local"
                        value={slot.starts_at}
                        onChange={(event) => updateAppointmentSlot(index, { starts_at: event.target.value })}
                      />
                    </Field>
                    <Field>
                      <label>End</label>
                      <input
                        type="datetime-local"
                        value={slot.ends_at}
                        onChange={(event) => updateAppointmentSlot(index, { ends_at: event.target.value })}
                      />
                    </Field>
                    <Field>
                      <label>Status</label>
                      <select
                        value={slot.status}
                        onChange={(event) =>
                          updateAppointmentSlot(index, {
                            status: event.target.value === 'inactive' ? 'inactive' : 'active',
                          })
                        }
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </Field>
                  </AppointmentSlotGrid>
                </AppointmentSlotCard>
              ))}
            </AppointmentSlotsList>

            <AppointmentSlotActions>
              <SecondaryActionButton type="button" onClick={addAppointmentSlot}>
                Add appointment slot
              </SecondaryActionButton>
            </AppointmentSlotActions>

            {appointmentSlotsDraft.length === 0 ? (
              <Hint>
                No appointment slots yet. Add slots here to make timed appointment signups available during event
                registration.
              </Hint>
            ) : null}

            <ButtonRow>
              <SecondaryActionButton
                type="button"
                onClick={handleSaveAppointmentSettings}
                disabled={isSavingEventDetails || isSavingAppointmentSlots || isLoadingAppointmentSlots}
              >
                {isSavingEventDetails || isSavingAppointmentSlots ? 'Saving…' : 'Save Appointment Settings'}
              </SecondaryActionButton>
            </ButtonRow>
          </AppointmentsTabCard>
        ) : null}

        {activeTab === 'ticketing' ? (
          <TicketingLayout>
            <TicketingMain>
              <TicketFormCard>
                <ManageCardHeader>
                  <h3>{editingTicketIndex === null ? 'Add New Ticket Type' : 'Edit Ticket Type'}</h3>
                </ManageCardHeader>
                {ticketValidationError ? <ErrorText>{ticketValidationError}</ErrorText> : null}

                <Field>
                  <label>Ticket Type Name</label>
                  <input
                    value={ticketEditor.name}
                    onChange={(event) => setTicketEditor((prev) => ({ ...prev, name: event.target.value }))}
                    placeholder="e.g. Student Pass, One Day Access"
                  />
                </Field>

                <EditorGrid>
                  <Field>
                    <label>{ticketEditor.is_sponsor_ticket ? 'Minimum sponsor amount ($)' : 'Price ($)'}</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={ticketPriceInput}
                      onChange={(event) => handleTicketPriceChange(event.target.value)}
                      onBlur={handleTicketPriceBlur}
                    />
                    <Hint>
                      {ticketEditor.is_sponsor_ticket
                        ? 'Purchasers can enter any amount at or above this minimum.'
                        : 'Use 0.00 for free tickets.'}
                    </Hint>
                  </Field>
                  {!ticketEditor.is_sponsor_ticket ? (
                    <Field>
                      <label>Deposit ($)</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        placeholder="0.00"
                        value={ticketDepositInput}
                        onChange={(event) => handleTicketDepositChange(event.target.value)}
                        onBlur={handleTicketDepositBlur}
                      />
                      <Hint>Use 0.00 to require full payment up front.</Hint>
                    </Field>
                  ) : null}
                  <Field>
                    <label>Quantity Available</label>
                    <input
                      type="number"
                      min={0}
                      value={ticketEditor.capacity}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({
                          ...prev,
                          capacity: Number(event.target.value || 0),
                        }))
                      }
                    />
                  </Field>
                  <Field>
                    <label>Per-order limit</label>
                    <input
                      type="number"
                      min={1}
                      value={ticketEditor.is_sponsor_ticket ? 1 : ticketEditor.per_order_limit}
                      disabled={ticketEditor.is_sponsor_ticket}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({
                          ...prev,
                          per_order_limit: Math.max(1, Number(event.target.value || 1)),
                        }))
                      }
                    />
                  </Field>
                  <Field>
                    <label>Status</label>
                    <select
                      value={ticketEditor.status}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({
                          ...prev,
                          status: event.target.value as EventTicketType['status'],
                        }))
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="sold_out">Sold out</option>
                    </select>
                  </Field>
                  <Field>
                    <label>Ticket Audience</label>
                    <select
                      value={ticketEditor.is_group_ticket ? 'group' : 'single'}
                      disabled={ticketEditor.is_sponsor_ticket}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({
                          ...prev,
                          is_group_ticket: event.target.value === 'group',
                          min_group_size: event.target.value === 'group' ? Math.max(6, prev.min_group_size || 6) : 6,
                        }))
                      }
                    >
                      <option value="single">Single</option>
                      <option value="group">Group</option>
                    </select>
                  </Field>
                  <Field>
                    <label>Ticket Type</label>
                    <select
                      value={ticketEditor.is_sponsor_ticket ? 'sponsor' : ticketEditor.is_special ? 'special' : 'normal'}
                      onChange={(event) => {
                        const nextType = event.target.value;
                        setTicketEditor((prev) => ({
                          ...prev,
                          is_sponsor_ticket: nextType === 'sponsor',
                          is_special: nextType === 'special',
                          is_group_ticket: nextType === 'sponsor' ? false : prev.is_group_ticket,
                          per_order_limit: nextType === 'sponsor' ? 1 : prev.per_order_limit,
                          deposit_amount_cents: nextType === 'sponsor' ? 0 : prev.deposit_amount_cents,
                        }));
                        if (nextType === 'sponsor') {
                          setTicketDepositInput('0.00');
                        }
                      }}
                    >
                      <option value="normal">Normal Ticket</option>
                      <option value="special">Special Ticket (special URL only)</option>
                      <option value="sponsor">Sponsor Ticket (custom amount)</option>
                    </select>
                  </Field>
                </EditorGrid>
                <Hint>All ticket prices are currently saved in USD.</Hint>

                {ticketEditor.is_group_ticket ? (
                  <Field>
                    <label>Minimum Group Size</label>
                    <input
                      type="number"
                      min={6}
                      value={Math.max(6, Number(ticketEditor.min_group_size || 6))}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({
                          ...prev,
                          min_group_size: Math.max(6, Number(event.target.value || 6)),
                        }))
                      }
                    />
                  </Field>
                ) : null}

                <Field>
                  <label>Description</label>
                  <textarea
                    rows={3}
                    value={ticketEditor.description}
                    onChange={(event) =>
                      setTicketEditor((prev) => ({ ...prev, description: event.target.value }))
                    }
                    placeholder="Describe what's included in this ticket tier..."
                  />
                </Field>

                <SwitchRow>
                  <label htmlFor="ticket-locked-toggle">Locked ticket</label>
                  <input
                    id="ticket-locked-toggle"
                    type="checkbox"
                    checked={ticketEditor.is_locked}
                    onChange={(event) =>
                      setTicketEditor((prev) => ({
                        ...prev,
                        is_locked: event.target.checked,
                        access_code: event.target.checked ? prev.access_code : '',
                      }))
                    }
                  />
                </SwitchRow>

                {ticketEditor.is_locked ? (
                  <Field>
                    <label>Access Code</label>
                    <input
                      value={ticketEditor.access_code}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({ ...prev, access_code: event.target.value }))
                      }
                      placeholder="Enter access code"
                    />
                  </Field>
                ) : null}

                <SwitchRow>
                  <label htmlFor="ticket-exclusive-toggle">Exclusive ticket</label>
                  <input
                    id="ticket-exclusive-toggle"
                    type="checkbox"
                    checked={ticketEditor.is_exclusive}
                    onChange={(event) =>
                      setTicketEditor((prev) => ({
                        ...prev,
                        is_exclusive: event.target.checked,
                        allowed_emails: event.target.checked ? prev.allowed_emails : '',
                      }))
                    }
                  />
                </SwitchRow>

                {ticketEditor.is_exclusive ? (
                  <Field>
                    <label>Allowed Emails (comma or new line)</label>
                    <textarea
                      rows={4}
                      value={ticketEditor.allowed_emails}
                      onChange={(event) =>
                        setTicketEditor((prev) => ({ ...prev, allowed_emails: event.target.value }))
                      }
                      placeholder={'first@example.com\nsecond@example.com'}
                    />
                    <Hint>Only these emails can purchase this exclusive ticket, once per email.</Hint>
                  </Field>
                ) : null}

                <SwitchRow>
                  <label htmlFor="ticket-requires-form-toggle">Requires registration form</label>
                  <input
                    id="ticket-requires-form-toggle"
                    type="checkbox"
                    checked={ticketEditor.requires_registration_form}
                    onChange={(event) =>
                      setTicketEditor((prev) => ({
                        ...prev,
                        requires_registration_form: event.target.checked,
                      }))
                    }
                  />
                </SwitchRow>
                {!ticketEditor.requires_registration_form ? (
                  <Hint>
                    Buyers can pay and check out without filling out the event&apos;s registration form (e.g. Vendor tickets).
                  </Hint>
                ) : null}

                <ButtonRow>
                  <SecondaryActionButton type="button" onClick={cancelTicketEditor}>
                    Cancel
                  </SecondaryActionButton>
                  <ActionButton type="button" onClick={upsertTicketType}>
                    {editingTicketIndex === null ? 'Create Ticket Type' : 'Update Ticket Type'}
                  </ActionButton>
                </ButtonRow>

                <ButtonRow>
                  <ActionButton
                    type="button"
                    onClick={() => saveTicketTypeChanges()}
                    disabled={isSavingTicketTypes}
                  >
                    {isSavingTicketTypes ? 'Saving…' : 'Save Ticket Types'}
                  </ActionButton>
                </ButtonRow>
              </TicketFormCard>

              <ManageCard>
                <TicketHeaderRow>
                  <h3>Active Ticket Types</h3>
                  <TicketCountBadge>{ticketTypesDraft.length} Types Defined</TicketCountBadge>
                </TicketHeaderRow>

                {isLoadingTicketTypes ? <p>Loading ticket types…</p> : null}

                {(ticketTypesDraft ?? []).length === 0 ? (
                  <Hint>No ticket types yet.</Hint>
                ) : (
                  <TicketTableWrap>
                    <TicketTable>
                      <thead>
                        <tr>
                          <th>Ticket Name</th>
                          <th>Price</th>
                          <th>Deposit</th>
                          <th>Available</th>
                          <th>Audience</th>
                          <th>Type</th>
                          <th>Status</th>
                          <th>Locked</th>
                          <th>Exclusive</th>
                          <th>Form</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                      {ticketTypesDraft.map((ticket, index) => {
                        const capacity = ticket.capacity ?? 0;
                        const soldRatio = ticket.status === 'sold_out' ? 100 : 0;
                        return (
                          <tr key={`ticket-row-${index}`}>
                            <td data-label="Ticket">
                              <TicketTdValue>
                                <TicketName>{ticket.name}</TicketName>
                                <TicketSub>{ticket.description || 'No description'}</TicketSub>
                                {ticket.is_special ? <TicketSub>Special URL ticket</TicketSub> : null}
                                {ticket.is_sponsor_ticket ? <TicketSub>Sponsor ticket (custom amount)</TicketSub> : null}
                                {ticket.is_locked && ticket.access_code ? (
                                  <TicketSub>Access code: {ticket.access_code}</TicketSub>
                                ) : null}
                                {ticket.is_exclusive ? (
                                  <TicketSub>
                                    Exclusive allowlist ({(ticket.allowed_emails ?? []).length})
                                  </TicketSub>
                                ) : null}
                                {ticket.requires_registration_form === false ? (
                                  <TicketSub>No registration form required</TicketSub>
                                ) : null}
                              </TicketTdValue>
                            </td>
                            <td data-label="Price">
                              <TicketTdValue>
                                {ticket.is_sponsor_ticket
                                  ? `${formatDollars(ticket.price_cents)} min`
                                  : formatDollars(ticket.price_cents)}
                              </TicketTdValue>
                            </td>
                            <td data-label="Deposit">
                              <TicketTdValue>
                                {Number(ticket.deposit_amount_cents || 0) > 0
                                  ? formatDollars(ticket.deposit_amount_cents)
                                  : 'Full'}
                              </TicketTdValue>
                            </td>
                            <td data-label="Available">
                              <TicketTdValue>
                                {capacity > 0 ? (
                                  <CapacityWrap>
                                    <CapacityBar>
                                      <CapacityFill style={{ width: `${soldRatio}%` }} />
                                    </CapacityBar>
                                    <CapacityText>{capacity}</CapacityText>
                                  </CapacityWrap>
                                ) : (
                                  'Unlimited'
                                )}
                              </TicketTdValue>
                            </td>
                            <td data-label="Audience">
                              <TicketTdValue>
                                {ticket.is_group_ticket
                                  ? `Group (min ${Math.max(6, Number(ticket.min_group_size || 6))})`
                                  : 'Single'}
                              </TicketTdValue>
                            </td>
                            <td data-label="Type">
                              <TicketTdValue>
                                {ticket.is_sponsor_ticket ? 'Sponsor' : ticket.is_special ? 'Special' : 'Normal'}
                              </TicketTdValue>
                            </td>
                            <td data-label="Status">
                              <TicketTdValue>
                                <StatusPill data-status={ticket.status}>{ticket.status}</StatusPill>
                              </TicketTdValue>
                            </td>
                            <td data-label="Locked">
                              <TicketTdValue>{ticket.is_locked ? 'Yes' : 'No'}</TicketTdValue>
                            </td>
                            <td data-label="Exclusive">
                              <TicketTdValue>{ticket.is_exclusive ? 'Yes' : 'No'}</TicketTdValue>
                            </td>
                            <td data-label="Form">
                              <TicketTdValue>{ticket.requires_registration_form === false ? 'No' : 'Yes'}</TicketTdValue>
                            </td>
                            <td data-label="Actions">
                              <TicketTdValue>
                                <TicketRowActions>
                                  <RowAction type="button" onClick={() => startEditTicket(index)}>
                                    Edit
                                  </RowAction>
                                  {ticket.is_special ? (
                                    <>
                                      <RowAction
                                        type="button"
                                        disabled={!ticket.id}
                                        onClick={() => handleCopyTicketRegistrationLink(ticket.id)}
                                      >
                                        Copy Special Link
                                      </RowAction>
                                      <RowAction
                                        type="button"
                                        disabled={!ticket.id}
                                        onClick={() => handleOpenTicketRegistrationLink(ticket.id)}
                                      >
                                        Open Special Link
                                      </RowAction>
                                    </>
                                  ) : null}
                                  <RowAction
                                    type="button"
                                    data-variant="danger"
                                    onClick={() => removeTicketType(index)}
                                  >
                                    Remove
                                  </RowAction>
                                </TicketRowActions>
                              </TicketTdValue>
                            </td>
                          </tr>
                        );
                      })}
                      </tbody>
                    </TicketTable>
                  </TicketTableWrap>
                )}
              </ManageCard>
            </TicketingMain>

            <TicketingSidebar>
              <SideCard>
                <ManageCardHeader>
                  <h3>Sales Windows</h3>
                </ManageCardHeader>
                <Field>
                  <label>Sale Start Date</label>
                  <input
                    type="date"
                    value={salesWindow.sales_start}
                    onChange={(event) =>
                      setSalesWindow((prev) => ({ ...prev, sales_start: event.target.value }))
                    }
                  />
                </Field>
                <Field>
                  <label>Sale End Date</label>
                  <input
                    type="date"
                    value={salesWindow.sales_end}
                    onChange={(event) =>
                      setSalesWindow((prev) => ({ ...prev, sales_end: event.target.value }))
                    }
                  />
                </Field>
              </SideCard>

              <CapacityCard>
                <h3>Capacity Summary</h3>
                <SummaryLabel>Total Venue Capacity</SummaryLabel>
                <SummaryBig>{ticketSummary.totalCapacity.toLocaleString()}</SummaryBig>
                <SummaryProgress>
                  <SummaryFill
                    style={{
                      width:
                        ticketSummary.totalCapacity > 0
                          ? `${Math.min((ticketSummary.registrations / ticketSummary.totalCapacity) * 100, 100)}%`
                          : '0%',
                    }}
                  />
                </SummaryProgress>
                <SummarySplit>
                  <div>
                    <SummaryLabel>Registrations</SummaryLabel>
                    <SummaryValue>{ticketSummary.registrations.toLocaleString()}</SummaryValue>
                  </div>
                  <div>
                    <SummaryLabel>Remaining</SummaryLabel>
                    <SummaryValue>{ticketSummary.remaining.toLocaleString()}</SummaryValue>
                  </div>
                </SummarySplit>
                <SummaryLabel>Projected Revenue</SummaryLabel>
                <SummaryBig>{formatDollars(ticketSummary.projectedRevenue)}</SummaryBig>
              </CapacityCard>

              <HelpCard>
                <h4>Need help with pricing?</h4>
                <p>Use simple tiers first (Early Bird, General, VIP), then optimize based on demand.</p>
                <p style={{ marginTop: '0.55rem' }}>
                  Registration totals are based on submitted registrations. Capacity and per-order limits are enforced at
                  registration time.
                </p>
              </HelpCard>
            </TicketingSidebar>
          </TicketingLayout>
        ) : null}

        {activeTab === 'form' || activeTab === 'waiver' ? (
          <FormTabCard>
            <ManageCardHeader>
              <h3>{activeTab === 'waiver' ? 'Waiver Questions' : 'Registration Form Builder'}</h3>
              <ActionButton type="button" onClick={() => addFormField(activeTab === 'waiver' ? 'waiver' : 'registration')}>
                {activeTab === 'waiver' ? '+ Add Waiver Question' : '+ Add Custom Field'}
              </ActionButton>
            </ManageCardHeader>
            {formValidationError ? <ErrorText>{formValidationError}</ErrorText> : null}

            {isLoadingOrderForm ? <p>Loading order form…</p> : null}

            {activeTab === 'waiver' ? (
              <SwitchRow>
                <label htmlFor="event-waiver-enabled">Enable waiver</label>
                <input
                  id="event-waiver-enabled"
                  type="checkbox"
                  checked={orderFormDraft.waiver_enabled === true}
                  onChange={(event) =>
                    setOrderFormDraft((prev) => ({ ...prev, waiver_enabled: event.target.checked }))
                  }
                />
              </SwitchRow>
            ) : (
              <SwitchRow>
                <label htmlFor="order-form-enabled">Enable custom order form</label>
                <input
                  id="order-form-enabled"
                  type="checkbox"
                  checked={!!orderFormDraft.is_enabled}
                  onChange={(event) =>
                    setOrderFormDraft((prev) => ({ ...prev, is_enabled: event.target.checked }))
                  }
                />
              </SwitchRow>
            )}

            <Hint>
              {activeTab === 'waiver'
                ? 'Turn the waiver on, then add the questions. Submitted waivers are on the Waiver List tab.'
                : 'Default attendee fields are built in. You can keep labels/types fixed and choose whether each one is required.'}
            </Hint>

            {activeTab === 'waiver' &&
            (orderFormDraft.fields ?? []).filter((field) => fieldGroupOf(field) === 'waiver').length === 0 ? (
              <Hint>No waiver questions yet.</Hint>
            ) : null}

            {(orderFormDraft.fields ?? [])
              .map((field, index) => ({ field, index }))
              .filter(({ field }) => fieldGroupOf(field) === (activeTab === 'waiver' ? 'waiver' : 'registration'))
              .map(({ field, index }, position, visibleFields) => {
              const isDefaultField = isDefaultOrderField(field);
              const isCollapsed = isFieldCollapsedByDefault(field, index);
              return (
              <EditorRow key={`order-form-field-${index}`} data-default={isDefaultField ? 'true' : 'false'}>
                <EditorHeader>
                  <EditorHeaderStart>
                    <EditorFieldName>{field.label || `Field ${index + 1}`}</EditorFieldName>
                    {isDefaultField ? <FieldBadge>Default Field</FieldBadge> : null}
                  </EditorHeaderStart>
                  <EditorHeaderActions>
                    <MiniButton
                      type="button"
                      onClick={() => moveFormField(index, 'up')}
                      disabled={position === 0}
                    >
                      ↑
                    </MiniButton>
                    <MiniButton
                      type="button"
                      onClick={() => moveFormField(index, 'down')}
                      disabled={position === visibleFields.length - 1}
                    >
                      ↓
                    </MiniButton>
                    <MiniButton type="button" onClick={() => toggleFieldCollapse(field, index)}>
                      {isCollapsed ? 'Expand' : 'Collapse'}
                    </MiniButton>
                  </EditorHeaderActions>
                </EditorHeader>
                {!isCollapsed ? (
                  <>
                <EditorGrid>
                  <Field>
                    <label>Label</label>
                    <input
                      value={field.label}
                      onChange={(event) => updateFormField(index, { label: event.target.value })}
                      placeholder="Dietary preference"
                      disabled={isDefaultField}
                    />
                  </Field>
                  <Field>
                    <label>Field Type</label>
                    <select
                      value={field.field_type}
                      onChange={(event) =>
                        updateFormField(index, {
                          field_type: event.target.value as EventOrderFormField['field_type'],
                        })
                      }
                      disabled={isDefaultField}
                    >
                      <option value="short_text">Short Text</option>
                      <option value="long_text">Long Text</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone</option>
                      <option value="number">Number</option>
                      <option value="select">Select</option>
                      <option value="multi_select">Multi-select</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="date">Date</option>
                    </select>
                  </Field>
                  <Field>
                    <label>Required</label>
                    <input
                      type="checkbox"
                      checked={!!field.is_required}
                      onChange={(event) => updateFormField(index, { is_required: event.target.checked })}
                    />
                  </Field>
                  {!isDefaultField ? (
                    <Field>
                      <label>Collect per attendee</label>
                      <input
                        type="checkbox"
                        checked={readCollectPerAttendee(field)}
                        onChange={(event) =>
                          updateFormField(index, {
                            validation: withCollectPerAttendeeValidation(field, event.target.checked),
                          })
                        }
                      />
                    </Field>
                  ) : null}
                </EditorGrid>

                <EditorGrid>
                  <Field>
                    <label>Placeholder</label>
                    <input
                      value={field.placeholder ?? ''}
                      onChange={(event) => updateFormField(index, { placeholder: event.target.value })}
                      disabled={isDefaultField}
                    />
                  </Field>
                </EditorGrid>

                <Field>
                  <label>Help text</label>
                  <textarea
                    rows={4}
                    value={field.help_text ?? ''}
                    onChange={(event) => updateFormField(index, { help_text: event.target.value })}
                    disabled={isDefaultField}
                    placeholder={
                      activeTab === 'waiver' || field.field_type === 'checkbox'
                        ? 'Shown with this question. For a checkbox, this is the text above the agreement.'
                        : 'Extra instructions shown with this field'
                    }
                  />
                </Field>

                {field.field_type === 'select' || field.field_type === 'multi_select' ? (
                  <FieldOptionsEditor
                    options={normalizeFieldOptions(field.options)}
                    onChange={(nextOptions) => updateFormField(index, { options: nextOptions })}
                    disabled={isDefaultField}
                  />
                ) : null}

                {!isDefaultField ? (
                  <InlineDangerButton type="button" onClick={() => removeFormField(index)}>
                    Remove
                  </InlineDangerButton>
                ) : null}
                  </>
                ) : null}
              </EditorRow>
            );
            })}

            {formPayloadError ? <ErrorText>{formPayloadError}</ErrorText> : null}

            <ButtonRow>
              <ActionButton type="button" onClick={() => void saveOrderFormChanges()} disabled={isSavingOrderForm}>
                {isSavingOrderForm ? 'Saving…' : activeTab === 'waiver' ? 'Save Waiver' : 'Save Order Form'}
              </ActionButton>
            </ButtonRow>
          </FormTabCard>
        ) : null}

        {activeTab === 'waiver-list' ? (
          <FormTabCard>
            <ManageCardHeader>
              <h3>Waiver List</h3>
            </ManageCardHeader>
            <EventWaiverList
              eventId={eventId}
              eventTitle={selectedEvent.title}
              enabled={orderFormDraft.waiver_enabled === true}
              fields={orderFormDraft.fields ?? []}
            />
          </FormTabCard>
        ) : null}

        {activeTab === 'registrants' ? (
          <RegistrantsTabCard>
            <PublicEventRegistrationsEventPage eventId={eventId} showBackLink={false} embedded />
          </RegistrantsTabCard>
        ) : null}
        {activeTab === 'tshirts' ? (
          <RegistrantsTabCard>
            <PublicEventRegistrationsEventPage eventId={eventId} showBackLink={false} embedded tshirtsOnly />
          </RegistrantsTabCard>
        ) : null}
        {activeTab === 'communication' ? (
          <RegistrantsTabCard>
            <PublicEventRegistrationsEventPage
              eventId={eventId}
              showBackLink={false}
              embedded
              communicationHistoryOnly
            />
          </RegistrantsTabCard>
        ) : null}
        {activeTab === 'reporting' ? (
          <ReportingTabCard>
            <ManageCardHeader>
              <h3>Reporting</h3>
            </ManageCardHeader>
            <Hint style={{ marginTop: 0 }}>
              Registration and revenue totals include confirmed orders only and exclude pending or failed states.
            </Hint>

            {isLoadingReporting ? <p>Loading reporting data…</p> : null}
            {!isLoadingReporting && reportingErrorMessage ? <ErrorText>{reportingErrorMessage}</ErrorText> : null}

            {!isLoadingReporting && !reportingErrorMessage ? (
              <>
                <ReportingMetricsGrid>
                  <ReportingMetricCard>
                    <ReportingMetricLabel>Gross Sales</ReportingMetricLabel>
                    <ReportingMetricValue>{formatDollars(reportingOverview.grossSales)}</ReportingMetricValue>
                    <ReportingMetricSub>
                      {reportingOverview.confirmedOrders.toLocaleString()} confirmed orders ·{' '}
                      {reportingOverview.confirmedTickets.toLocaleString()} tickets
                    </ReportingMetricSub>
                  </ReportingMetricCard>
                  <ReportingMetricCard>
                    <ReportingMetricLabel>Total Registrations</ReportingMetricLabel>
                    <ReportingMetricValue>{reportingOverview.totalRegistrations.toLocaleString()}</ReportingMetricValue>
                  </ReportingMetricCard>
                  <ReportingMetricCard>
                    <ReportingMetricLabel>Total Tickets</ReportingMetricLabel>
                    <ReportingMetricValue>{reportingOverview.totalTickets.toLocaleString()}</ReportingMetricValue>
                  </ReportingMetricCard>
                  <ReportingMetricCard>
                    <ReportingMetricLabel>Checked In</ReportingMetricLabel>
                    <ReportingMetricValue>{reportingOverview.checkedInRegistrations.toLocaleString()}</ReportingMetricValue>
                    <ReportingMetricSub>{reportingOverview.showUpRate.toFixed(1)}% show-up rate</ReportingMetricSub>
                  </ReportingMetricCard>
                </ReportingMetricsGrid>

                <ReportingSection>
                  <ReportingSectionHeader>
                    <h4>Ticket Type Breakdown</h4>
                  </ReportingSectionHeader>
                  {reportingTicketTypeBreakdown.length === 0 ? (
                    <EmptyState>No registrations yet for this event.</EmptyState>
                  ) : (
                    <TicketTableWrap>
                      <TicketTable>
                        <thead>
                          <tr>
                            <th>Ticket Type</th>
                            <th>Registrations</th>
                            <th>Tickets</th>
                            <th>Gross (Confirmed)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportingTicketTypeBreakdown.map((row) => (
                            <tr key={row.ticketTypeName}>
                              <td data-label="Ticket Type">
                                <TicketTdValue>{row.ticketTypeName}</TicketTdValue>
                              </td>
                              <td data-label="Registrations">
                                <TicketTdValue>{row.registrations.toLocaleString()}</TicketTdValue>
                              </td>
                              <td data-label="Tickets">
                                <TicketTdValue>{row.quantity.toLocaleString()}</TicketTdValue>
                              </td>
                              <td data-label="Gross (Confirmed)">
                                <TicketTdValue>{formatDollars(row.grossCents)}</TicketTdValue>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </TicketTable>
                    </TicketTableWrap>
                  )}
                </ReportingSection>

                <ReportingSection>
                  <ReportingSectionHeader>
                    <h4>Ticket Change Financial Summary</h4>
                  </ReportingSectionHeader>
                  {reportingTicketChanges.length === 0 ? (
                    <EmptyState>No ticket changes recorded yet.</EmptyState>
                  ) : (
                    <>
                      <ReportingMetricsGrid>
                        <ReportingMetricCard>
                          <ReportingMetricLabel>Upgrade Delta</ReportingMetricLabel>
                          <ReportingMetricValue>{formatDollars(reportingTicketChangeFinancials.upgradeDelta)}</ReportingMetricValue>
                        </ReportingMetricCard>
                        <ReportingMetricCard>
                          <ReportingMetricLabel>Downgrade Delta</ReportingMetricLabel>
                          <ReportingMetricValue>-{formatDollars(reportingTicketChangeFinancials.downgradeDelta)}</ReportingMetricValue>
                        </ReportingMetricCard>
                        <ReportingMetricCard>
                          <ReportingMetricLabel>Paid Delta</ReportingMetricLabel>
                          <ReportingMetricValue>{formatDollars(reportingTicketChangeFinancials.paidDelta)}</ReportingMetricValue>
                        </ReportingMetricCard>
                        <ReportingMetricCard>
                          <ReportingMetricLabel>Pending Delta</ReportingMetricLabel>
                          <ReportingMetricValue>{formatDollars(reportingTicketChangeFinancials.pendingDelta)}</ReportingMetricValue>
                          <ReportingMetricSub>
                            Not required: {formatDollars(reportingTicketChangeFinancials.notRequiredDelta)}
                          </ReportingMetricSub>
                        </ReportingMetricCard>
                      </ReportingMetricsGrid>
                      <TicketTableWrap>
                        <TicketTable>
                          <thead>
                            <tr>
                              <th>Payment Status</th>
                              <th>Delta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportingTicketChangeFinancials.statusTotals.map((row) => (
                              <tr key={row.status}>
                                <td data-label="Payment Status">
                                  <TicketTdValue>{formatPaymentStatusLabel(row.status)}</TicketTdValue>
                                </td>
                                <td data-label="Delta">
                                  <TicketTdValue>{formatDollars(row.delta)}</TicketTdValue>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </TicketTable>
                      </TicketTableWrap>
                    </>
                  )}
                </ReportingSection>
              </>
            ) : null}
          </ReportingTabCard>
        ) : null}
        {activeTab === 'admins' ? (
          <RegistrantsTabCard>
            <EventAdminsPanel eventId={eventId} />
          </RegistrantsTabCard>
        ) : null}
      </ManageGrid>
      {isPublishQrModalOpen && websiteEventUrl ? (
        <PublishQrOverlay onClick={() => setIsPublishQrModalOpen(false)}>
          <PublishQrModal role="dialog" aria-modal="true" onClick={(event) => event.stopPropagation()}>
            <ManageCardHeader>
              <h3>Event published</h3>
              <SecondaryActionButton type="button" onClick={() => setIsPublishQrModalOpen(false)}>
                Close
              </SecondaryActionButton>
            </ManageCardHeader>
            <RegistrationQrLayout>
              <RegistrationQrBlock ref={modalQrContainerRef}>
                <QRCode
                  value={websiteEventUrl}
                  size={180}
                  type="svg"
                  color={EVENT_QR_FOREGROUND}
                  bgColor={EVENT_QR_BACKGROUND}
                />
              </RegistrationQrBlock>
              <RegistrationQrContent>
                <RegistrationQrHint>
                  Share or scan this QR code to open this event&apos;s registration page.
                </RegistrationQrHint>
                <RegistrationQrLink
                  href={websiteEventUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {websiteEventUrl}
                </RegistrationQrLink>
                <RegistrationQrActions>
                  <SecondaryActionButton type="button" onClick={handleCopyRegistrationLink}>
                    Copy Link
                  </SecondaryActionButton>
                  <SecondaryActionButton type="button" onClick={handleDownloadQrImage}>
                    Download 2048x2048
                  </SecondaryActionButton>
                  <ActionButton type="button" onClick={handleOpenRegistrationLink}>
                    Open Link
                  </ActionButton>
                </RegistrationQrActions>
              </RegistrationQrContent>
            </RegistrationQrLayout>
          </PublishQrModal>
        </PublishQrOverlay>
      ) : null}
    </Page>
  );
};

export default EventSetupPage;

const Page = styled.div`
  background: var(--background);
  padding: 2rem;
  min-height: 100%;
  color: var(--onSurface);

  @media (max-width: 700px) {
    padding: 0.8rem;
  }
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const ManageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const TicketingLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 1rem;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const TicketingMain = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const TicketingSidebar = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
`;

const SideCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.25rem;
`;

const TicketHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  h3 {
    margin: 0;
    font-size: 1rem;
    color: var(--onSurface);
  }
`;

const TicketCountBadge = styled.span`
  border-radius: 999px;
  background: var(--outline);
  color: var(--onSurfaceVariant);
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.2rem 0.6rem;
`;

const TicketTableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const TicketTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 0.65rem 0.5rem;
    border-top: 1px solid var(--outline);
    text-align: left;
    font-size: 0.86rem;
    vertical-align: top;
  }

  td {
    color: var(--onSurface);
  }

  th {
    font-size: 0.72rem;
    color: var(--onSurfaceVariant);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-top: none;
  }
  @media (max-width: 768px) {
    th,
    td {
      padding: 0.5rem 0.4rem;
      font-size: 0.8rem;
    }
  }

  @media (max-width: 639px) {
    thead {
      display: none;
    }

    tbody tr {
      display: block;
      border: 1px solid var(--outline);
      border-radius: 10px;
      margin-bottom: 0.65rem;
      overflow: hidden;
      background: var(--surface);
    }

    tbody tr td {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 0.65rem;
      border-top: 1px solid var(--outline);
      text-align: right;
      padding: 0.55rem 0.65rem;
    }

    tbody tr td:first-child {
      border-top: none;
    }

    tbody tr td::before {
      content: attr(data-label);
      font-weight: 700;
      font-size: 0.65rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--onSurfaceVariant);
      text-align: left;
      flex: 0 0 auto;
      max-width: 42%;
      padding-top: 0.12rem;
    }

  }
`;

const TicketTdValue = styled.div`
  flex: 1;
  min-width: 0;
  text-align: right;
  color: inherit;

  @media (min-width: 640px) {
    text-align: inherit;
  }
`;

const TicketName = styled.div`
  font-weight: 700;
  color: var(--onSurface);
`;

const TicketSub = styled.div`
  color: var(--onSurfaceVariant);
  font-size: 0.75rem;
  margin-top: 0.15rem;
`;

const CapacityWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const CapacityBar = styled.div`
  width: 64px;
  height: 6px;
  border-radius: 999px;
  background: var(--outline);
  overflow: hidden;
`;

const CapacityFill = styled.div`
  height: 100%;
  background: var(--primary);
`;

const CapacityText = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: capitalize;

  &[data-status='active'] {
    background: var(--successContainer);
    color: var(--onSuccessContainer);
  }

  &[data-status='inactive'] {
    background: var(--surfaceVariant);
    color: var(--onSurfaceVariant);
  }

  &[data-status='sold_out'] {
    background: var(--errorContainer);
    color: var(--onErrorContainer);
  }
`;

const RowAction = styled.button`
  border: none;
  background: transparent;
  color: var(--primary);
  cursor: pointer;
  font-size: 0.8rem;
  margin-right: 0.35rem;

  &[data-variant='danger'] {
    color: var(--error);
  }
`;

const TicketRowActions = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-end;

  @media (max-width: 639px) {
    justify-content: flex-end;
    width: 100%;
  }

  ${RowAction} {
    margin-right: 0;
    min-height: 44px;
    padding: 0.35rem 0.5rem;
  }
`;

const CapacityCard = styled.div`
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 10px;
  padding: 1.25rem;

  h3 {
    margin: 0 0 0.75rem;
    font-size: 1rem;
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.9;
  font-weight: 700;
`;

const SummaryBig = styled.div`
  font-size: 2rem;
  font-weight: 800;
  margin: 0.1rem 0 0.55rem;
  line-height: 1.1;
`;

const SummaryValue = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  line-height: 1.1;
`;

const SummaryProgress = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.4);
  overflow: hidden;
  margin: 0.4rem 0 0.85rem;
`;

const SummaryFill = styled.div`
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
`;

const SummarySplit = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
`;

const HelpCard = styled.div`
  border: 1px dashed var(--outline);
  border-radius: 10px;
  padding: 1rem;
  background: var(--surfaceVariant);

  h4 {
    margin: 0 0 0.35rem;
    font-size: 0.92rem;
    color: var(--onSurface);
  }

  p {
    margin: 0;
    color: var(--onSurfaceVariant);
    font-size: 0.82rem;
    line-height: 1.45;
  }
`;

const TabsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;

  @media (max-width: 700px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    gap: 0.45rem;
    padding: 0 0 0.4rem;
    margin-bottom: 0.85rem;
    scrollbar-width: thin;
  }
`;

const TabButton = styled.button`
  padding: 0.42rem 0.78rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surface);
  cursor: pointer;
  font-weight: 700;
  font-size: 0.82rem;
  color: var(--onSurfaceVariant);
  transition: all 0.15s ease;

  &[data-active='true'] {
    background: var(--primary);
    color: var(--onPrimary);
    border-color: var(--primaryDark);
  }

  @media (max-width: 700px) {
    flex: 0 0 auto;
    white-space: nowrap;
    min-height: 44px;
    padding: 0.45rem 0.9rem;
  }
`;

const ManageCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.25rem;
`;

const RegistrationQrCard = styled(ManageCard)`
  margin-top: 0.9rem;
`;

const RegistrationQrLayout = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 1rem;
  align-items: start;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
  }
`;

const RegistrationQrBlock = styled.div`
  width: fit-content;
  padding: 0.5rem;
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
`;

const RegistrationQrContent = styled.div`
  min-width: 0;
`;

const RegistrationQrHint = styled.p`
  margin: 0 0 0.55rem;
  color: var(--onSurfaceVariant);
  font-size: 0.9rem;
`;

const RegistrationQrLink = styled.a`
  display: block;
  color: var(--primary);
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  word-break: break-all;
  margin-bottom: 0.75rem;

  &:hover {
    text-decoration: underline;
  }
`;

const RegistrationQrActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ManageCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;

  h3 {
    margin: 0;
    font-size: 1rem;
    color: var(--onSurface);
  }

  @media (max-width: 700px) {
    flex-wrap: wrap;
  }
`;

const EditorRow = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 0.65rem 0.7rem;
  margin-bottom: 0.6rem;
  background: var(--surfaceVariant);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &[data-default='true'] {
    border-color: var(--primary);
    background: var(--surface);
  }

  &:hover {
    border-color: var(--primary);
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.08);
  }
`;

const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  margin-bottom: 0.45rem;

  @media (max-width: 700px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const EditorHeaderStart = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const EditorHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const EditorFieldName = styled.h4`
  margin: 0;
  font-size: 0.88rem;
  color: var(--onSurface);
`;

const FieldBadge = styled.span`
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--primary);
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.16rem 0.5rem;
  white-space: nowrap;
`;

const MiniButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  padding: 0.2rem 0.55rem;
  cursor: pointer;
  min-width: 2rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const EditorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.45rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const GeneralTopGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem;
  margin-bottom: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const GeneralMetaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 0.75rem;
  margin: 0.85rem 0;
`;

const GeneralSectionTitle = styled.h3`
  margin: 0 0 0.9rem;
  color: var(--onSurface);
  font-size: 1.15rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 0.45rem;
`;

const StatusDot = styled.span`
  color: var(--primary);
  font-size: 0.95rem;
`;

const BannerPreview = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  border: 1px solid var(--outline);
  margin: 0.35rem 0 0.85rem;
  background: var(--surfaceVariant);
  background-size: cover;
  background-position: center;
  position: relative;
  overflow: hidden;
`;

const BannerOverlayLabel = styled.label`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  border: 1px solid var(--outline);
  background: rgba(0, 0, 0, 0.5);
  color: var(--text);
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 0.9rem;
  cursor: pointer;
  backdrop-filter: blur(2px);

  input {
    display: none;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;

  label {
    font-size: 0.74rem;
    font-weight: 600;
    color: var(--onSurfaceVariant);
    letter-spacing: 0.01em;
  }

  input,
  select,
  textarea {
    border: 1px solid var(--outline);
    border-radius: 10px;
    padding: 0.42rem 0.58rem;
    font-size: 0.84rem;
    background: var(--surface);
    color: var(--onSurface);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  input:focus,
  select:focus,
  textarea:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--onSurfaceVariant);
  }

  textarea {
    resize: vertical;
    min-height: 60px;
  }
`;

const MinistryDropdownContainer = styled.div`
  position: relative;
`;

const MinistryDropdownList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0.35rem 0;
  position: absolute;
  top: calc(100% + 0.2rem);
  left: 0;
  right: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 220px;
  overflow: auto;
  background: var(--surface);
  z-index: 20;
`;

const MinistryDropdownItem = styled.li`
  padding: 0.5rem 0.72rem;
  cursor: pointer;
  color: var(--onSurface);

  &:hover {
    background: var(--surfaceVariant);
  }
`;

const MinistryDropdownEmpty = styled.li`
  padding: 0.5rem 0.72rem;
  color: var(--onSurfaceVariant);
`;

const TicketFormCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.25rem;

  @media (max-width: 700px) {
    padding: 1rem;
  }

  ${Field} {
    gap: 0.38rem;
    margin-bottom: 0.35rem;
  }

  ${Field} label {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--onSurfaceVariant);
  }

  ${Field} input,
  ${Field} select,
  ${Field} textarea {
    background: var(--surface);
    border: 1px solid var(--outline);
    border-radius: 8px;
    min-height: 44px;
    padding: 0.6rem 0.72rem;
    color: var(--onSurface);
  }

  ${Field} textarea {
    min-height: 92px;
  }

  ${EditorGrid} {
    gap: 0.85rem;
    margin: 0.55rem 0 0.9rem;
  }
`;

const GeneralInfoCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 1.25rem;

  ${Field} {
    gap: 0.38rem;
    margin-bottom: 0.35rem;
  }

  ${Field} label {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--onSurfaceVariant);
  }

  ${Field} input,
  ${Field} select,
  ${Field} textarea {
    background: var(--surface);
    border: 1px solid var(--outline);
    border-radius: 8px;
    min-height: 44px;
    padding: 0.6rem 0.72rem;
    color: var(--onSurface);
  }

  ${Field} textarea {
    min-height: 92px;
  }

  ${EditorGrid},
  ${GeneralTopGrid},
  ${GeneralMetaGrid} {
    gap: 0.85rem;
  }
`;

const AppointmentsTabCard = styled(GeneralInfoCard)`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const AppointmentSlotsList = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const AppointmentSlotCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
  padding: 0.85rem;
`;

const AppointmentSlotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: var(--onSurface);
`;

const AppointmentSlotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const AppointmentSlotActions = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const FormTabCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);

  ${Field} {
    gap: 0.25rem;
    margin-bottom: 0.2rem;
  }

  ${Field} label {
    font-size: 0.74rem;
    font-weight: 700;
    color: var(--onSurfaceVariant);
  }

  ${Field} input,
  ${Field} select,
  ${Field} textarea {
    background: var(--surface);
    border: 1px solid var(--outline);
    border-radius: 10px;
    min-height: 38px;
    padding: 0.45rem 0.62rem;
    color: var(--onSurface);
  }

  ${Field} textarea {
    min-height: 80px;
  }

  ${EditorGrid} {
    gap: 0.6rem;
    margin: 0.4rem 0 0.6rem;
  }
`;

/** Slightly tighter shell when registrants UI is embedded in event setup (avoids double padding on phones). */
const RegistrantsTabCard = styled(FormTabCard)`
  @media (max-width: 700px) {
    padding: 0.55rem 0.45rem;
    border-radius: 10px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }
`;

const ReportingTabCard = styled(FormTabCard)`
  display: grid;
  gap: 1rem;
`;

const ReportingMetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 1120px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ReportingMetricCard = styled.div`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surfaceVariant);
  padding: 0.8rem;
`;

const ReportingMetricLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--onSurfaceVariant);
`;

const ReportingMetricValue = styled.div`
  margin-top: 0.35rem;
  font-size: 1.4rem;
  line-height: 1.1;
  font-weight: 800;
  color: var(--onSurface);
`;

const ReportingMetricSub = styled.div`
  margin-top: 0.2rem;
  font-size: 0.76rem;
  color: var(--onSurfaceVariant);
`;

const ReportingSection = styled.section`
  border: 1px solid var(--outline);
  border-radius: 12px;
  background: var(--surface);
  padding: 0.9rem;
`;

const ReportingSectionHeader = styled.div`
  margin-bottom: 0.6rem;

  h4 {
    margin: 0;
    font-size: 0.95rem;
    color: var(--onSurface);
  }
`;

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.55rem;
  padding: 0.55rem 0.65rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surfaceVariant);

  label {
    font-weight: 600;
    color: var(--onSurface);
    font-size: 0.86rem;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: var(--primary);
  }
`;

const Hint = styled.p`
  font-size: 0.84rem;
  color: var(--onSurfaceVariant);
  margin: 0.25rem 0 0.65rem;
  line-height: 1.35;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.6rem;

  @media (max-width: 700px) {
    width: 100%;
    justify-content: stretch;
    flex-wrap: wrap;
  }
`;

const ActionButton = styled.button`
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--primaryDark);
  background: var(--primary);
  color: var(--onPrimary);
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const SecondaryActionButton = styled.button`
  padding: 0.55rem 0.9rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--outline);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 700px) {
    width: 100%;
  }
`;

const InlineDangerButton = styled.button`
  border: 1px solid var(--error);
  color: var(--onErrorContainer);
  background: var(--errorContainer);
  border-radius: 8px;
  padding: 0.35rem 0.6rem;
  font-weight: 600;
  cursor: pointer;
`;

const FieldOptionsWrap = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem;
  background: var(--surface);
  margin-bottom: 0.55rem;
`;

const OptionHintText = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--onSurfaceVariant);
  margin-bottom: 0.4rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const OptionChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-bottom: 0.45rem;
`;

const OptionChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  border-radius: 999px;
  border: 1px solid var(--outline);
  background: var(--surfaceVariant);
  color: var(--onSurface);
  font-size: 0.78rem;
  padding: 0.18rem 0.24rem 0.18rem 0.56rem;
`;

const OptionRemoveButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  border-radius: 999px;
  width: 20px;
  height: 20px;
  cursor: pointer;
  font-size: 0.92rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover:not(:disabled) {
    background: var(--outline);
    color: var(--onSurface);
  }
`;

const OptionEmptyText = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.78rem;
`;

const OptionInputRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.4rem;
  margin-bottom: 0.4rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const OptionTextInput = styled.input`
  border: 1px solid var(--outline);
  border-radius: 10px;
  min-height: 36px;
  padding: 0.4rem 0.6rem;
  font-size: 0.82rem;
  background: var(--surface);
  color: var(--onSurface);

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
  }
`;

const OptionBulkArea = styled.textarea`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.45rem 0.6rem;
  font-size: 0.82rem;
  background: var(--surface);
  color: var(--onSurface);
  resize: vertical;
  min-height: 72px;
  margin-bottom: 0.2rem;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
  }
`;

const ErrorText = styled.p`
  margin: 0.25rem 0;
  color: var(--error);
  font-weight: 600;
`;

const InlineNotice = styled.div<{ $kind: 'success' | 'error' | 'info' }>`
  border: 1px solid
    ${({ $kind }) => ($kind === 'error' ? 'var(--error)' : $kind === 'success' ? '#047857' : 'var(--outline)')};
  background: ${({ $kind }) =>
    $kind === 'error'
      ? 'color-mix(in srgb, var(--error) 10%, var(--surface))'
      : $kind === 'success'
        ? 'color-mix(in srgb, #047857 14%, var(--surface))'
        : 'color-mix(in srgb, var(--primary) 10%, var(--surface))'};
  color: var(--onSurface);
  border-radius: 10px;
  padding: 0.7rem 0.9rem;
  margin-bottom: 0.9rem;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  padding: 1rem;
  border: 1px dashed var(--outline);
  border-radius: 10px;
  color: var(--onSurfaceVariant);
  margin-bottom: 1.5rem;
  background: var(--surfaceVariant);
`;

const PublishQrOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: grid;
  place-items: center;
  z-index: 1200;
  padding: 1rem;
`;

const PublishQrModal = styled.div`
  width: min(680px, 100%);
  max-height: calc(100vh - 2rem);
  overflow: auto;
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.15rem;
  box-shadow: 0 20px 36px rgba(15, 23, 42, 0.22);
`;

function isConfirmedOrderStatus(status?: string | null) {
  const normalized = String(status || '').trim().toLowerCase();
  return normalized === 'confirmed';
}

function formatPaymentStatusLabel(status: string) {
  const normalized = status.trim().replace(/[_-]+/g, ' ');
  if (!normalized) return 'Not Required';
  return normalized.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDollars(cents?: number | null) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format((cents ?? 0) / 100);
}

function sanitizeTicketPriceInput(value: string) {
  const normalized = value.replace(/[^\d.]/g, '');
  if (!normalized) return '';
  if (normalized === '.') return '0.';
  const [wholePart = '', ...decimalPieces] = normalized.split('.');
  const whole = wholePart ? wholePart.replace(/^0+(?=\d)/, '') || '0' : '0';
  if (decimalPieces.length === 0) return whole;
  const decimal = decimalPieces.join('').slice(0, 2);
  return `${whole}.${decimal}`;
}

function parseTicketPriceInputToCents(value: string) {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return Math.round(parsed * 100);
}

function formatTicketPriceInput(cents?: number | null) {
  const safeCents = Number.isFinite(cents) ? Number(cents) : 0;
  return (Math.max(0, safeCents) / 100).toFixed(2);
}

function normalizeExclusiveEmailList(values: string[] | null | undefined) {
  return Array.from(
    new Set(
      (values ?? [])
        .map((value) => String(value || '').trim().toLowerCase())
        .filter((value) => /^\S+@\S+\.\S+$/.test(value))
    )
  );
}

function normalizeExclusiveEmailsInput(value: string) {
  return normalizeExclusiveEmailList(value.split(/[\n,]/g));
}
